/**
 * useAgentChatEngine — Agent Chat "대화 엔진".
 *
 * ⚠️ 이 파일은 **pages/** 를 import 하지 않는다.** 화면 지식은 다섯 입구로만 들어온다:
 *     · publish 채널   — AgentChatHostContext.useProvideAgentChat (렌더에 필요한 값)
 *     · 정책 채널      — agentChatAdapter (단계 구조·시나리오 상한·백엔드 payload 도메인 필드·
 *                        슬래시 분류·되돌리기 불가 판정)
 *     · pull 채널      — getLive() (질문 보내는 순간에만 필요한 무거운 값)
 *     · 액션 레지스트리 — AgentActions.useProvideAgentActions
 *     · 화면 상태      — 버전 채널(screenVersionChannel)과 redux viewStates 의 허용 목록 키
 *                        (screenConditions). 비시나리오 화면에서 보내는 순간에 읽는다.
 *
 * ⚠️ 엔진은 앱 전체에 **하나뿐**이고 뷰는 여럿이다(우측 패널 + 홈 AI 어시스턴트 카드).
 *   예전에는 대화 상태가 전부 AgentChatPanel 인스턴스 로컬이라 뷰가 둘이 될 수 없었다.
 *   지금은 이 훅이 대화를 소유하고, 뷰는 구독 + 명령만 한다.
 *
 * ⚠️ 세션 정책: **대화의 경계는 "앱 로드"다.**
 *   - 엔진 마운트 = 항상 새 대화(웰컴 1건 + 새 세션 id). 서버의 마지막 세션을 자동으로 이어받지 않는다.
 *   - 새로고침·재로그인처럼 흐름이 끊기면 새 대화로 시작한다. 지난 대화는 기록에서 사용자가
 *     **명시적으로** 고른다(loadChatSession). 이게 유일한 복원 경로다.
 *
 * 뷰와의 경계:
 *   - 입력창 초안(input)은 **뷰마다 따로**다. 엔진이 되돌려야 할 때는 draftSignal 로 알린다
 *     (중지 롤백·busy 반려). viewId 를 함께 실어 "보낸 뷰"에만 되돌려 준다.
 *   - 슬래시 팔레트·스크롤·포커스·패널 폭은 뷰 소유. 엔진이 이를 무효화해야 할 때는
 *     uiEpoch / historyEpoch 를 올린다.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "react-redux";

import agentChatTransport from "@plannel/components/agent/agentChatTransport";
import { useAgentActions } from "@plannel/components/agent/AgentActions";
import { useAgentChatApiRef, useAgentChatScreen } from "@plannel/components/agent/AgentChatHostContext";
import { resolveAdapter } from "@plannel/components/agent/agentChatAdapter";
import { ACTION, ACTION_ORIGIN, IRREVERSIBLE_VERBS, chatErrorText } from "@plannel/components/agent/agentChatProtocol";
import {
  SUPPORTED_CHAT_MODULES, STATUS_STEPS, newChatSessionId,
  CONSULTED_EXCLUDE_LABELS, choiceInputValueOf,
  hydrateRestoredMessages, pendingOwnerKey, isPendingOwner,
} from "@plannel/components/agent/agentChatConfig";
import { resolveChatModule } from "@plannel/components/agent/screenChatModule";
import {
  getScreenVersionsSnapshot, screenVersionFields, screenVersionsPayload,
} from "@plannel/components/agent/screenVersionChannel";
import {
  buildScreenConditions, pickConditionRaw, screenConditionsPayload,
} from "@plannel/components/agent/screenConditions";
import { traceLabel, keyedText } from "@plannel/components/ai/aiTraceArgs";
import debug from "@plannel/utils/debug-util";

const EMPTY_GETTER = () => ({});
const GENERAL_MOD_CODE = "GENERAL";

/** 뷰가 호출할 수 있는 명령 목록 — 뷰별 래퍼를 만들 때 이 이름들로 감싼다. */
const API_NAMES = [
  "send", "abortChat", "resetChat", "clearConversation",
  "loadChatSession", "deleteChatSession", "refreshSessions",
  "sendFeedback", "handleChoiceClick", "goBackChoice", "dismissChoices",
  "submitChoiceInput", "setChoiceInput", "runLiveAction", "runPaletteAction",
  "requestParamTarget", "typeMessage", "requestScreenNav", "notifyBlocked",
  "openScreenLink",
];

export default function useAgentChatEngine() {
  const { t } = useTranslation();
  // 화면 조건은 보내는 순간에만 읽는다 — 구독하지 않는다(칩 구독은 AgentChatContextChips 몫).
  const store = useStore();

  // ── 화면 컨텍스트(publish 채널) ─────────────────────────────────────────────
  //   예전엔 호스트가 prop 으로 중계했지만, 엔진이 앱 셸에 있으므로 직접 읽는다.
  const screen = useAgentChatScreen();
  // 시나리오 화면이 발행한 모듈 코드. 이번 턴을 보낼 챗 모듈은 아니다 — 그쪽은 화면의
  //   업무 영역이라 send() 에서 메뉴 계층으로 정한다(chatModule). 이 값은 세션 목록·삭제처럼
  //   세션 id 로 이미 특정되는 호출의 경로 조각으로만 쓴다.
  const modCode = screen?.modCode || GENERAL_MOD_CODE;
  const currentStep = screen?.currentStep ?? 0;
  const stepLabel = screen?.stepLabel;
  const scenarios = screen?.scenarios;
  const composeScenarioCount = screen?.composeScenarioCount ?? 1;
  const composeStarted = screen?.composeStarted ?? false;
  const onExecuteAction = screen?.onExecuteAction;
  const getLive = screen?.getLive ?? EMPTY_GETTER;
  const screenKey = screen?.scopeId ?? "__global__";
  // 지연 적용 보관값의 주인 표시. 🔴 ref 가 아니라 렌더 스코프 값이어야 한다 — send() 는 await 뒤에도
  //   **보낸 시점** 클로저로 보관하므로, 응답을 기다리는 사이 탭을 옮겨도 보낸 화면이 주인으로 찍힌다. [Q-131]
  const ownerKey = pendingOwnerKey(screen);
  // (modLabel·periodLabel 등 표시용 값은 엔진이 쓰지 않는다 — 뷰가 useAgentChatScreen 으로 직접 읽는다)
  // 화면 정책 채널 — 단계 구조·시나리오 상한·백엔드 payload 도메인 필드 등. 미제공 화면은 무정책 기본값.
  const adapter = resolveAdapter(screen?.adapter);
  const maxScenarios = adapter.maxScenarios;
  const stepNameKeys = adapter.steps?.nameKeys ?? [];
  const composeStep = adapter.steps?.composeStep ?? null;

  // ── 대화 상태 ──────────────────────────────────────────────────────────────
  /*
    최초 진입 인사말. `greeting: true` 로 표시해 두는 이유 —
      이 문구는 "입력창에 / 를 치면 액션 목록이 나온다"는 **패널의 사용법 안내**다.
      홈 AI 어시스턴트 카드는 슬래시 팔레트가 없고, 게다가 거기서는 사용자가 질문을 눌러
      대화가 시작되므로 "받지도 않은 인사"가 자기 질문 위에 먼저 놓인다.
      대화 내용이 아니라 뷰의 안내이므로, 뷰가 그릴지 말지 고를 수 있게 태그만 붙인다.
  */
  const initMsg = t("agentGreetInit");
  const [messages, setMessages] = useState(() => [{ role: "a", text: initMsg, greeting: true }]);
  // 최신 메시지 미러 — 비동기 콜백(세션 발급 .then 등)이 클로저에 갇힌 옛 값 대신 현재 대화를 본다.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const [typing, setTyping] = useState(false);
  const [statusText, setStatusText] = useState("");
  const statusTimerRef = useRef(null);
  const streamTimerRef = useRef(null);

  // 대화 세대(generation) — /clear 로 대화를 초기화할 때마다 증가. in-flight 응답이 초기화된
  //   새 대화에 뒤늦게 끼어드는 레이스를 막는 가드로 send() 에서 캡처·비교한다.
  const chatGenRef = useRef(0);
  // 진행 중인 챗 SSE 스트림 — 언마운트·/clear 시 끊는다(안 끊으면 소켓이 살아남아 응답이 유실된다).
  const chatAbortRef = useRef(null);
  // 서버 왕복이 진행 중인지 — 중복 전송 가드. typing 은 첫 델타에서 꺼지는 '타이핑 점' 표시용이라
  //   재사용할 수 없다. 백엔드는 세션당 Agent 인스턴스를 재사용해 동시 호출을 거절하므로 여기서 막는다.
  const chatBusyRef = useRef(false);
  const [chatBusy, setChatBusy] = useState(false);
  // 지금 도는 턴 번호(0 = 없음) — 도구 이력 그룹을 접을 시점의 유일한 신호다.
  //   말풍선에 '끝났음' 표시를 심지 않는다: 중지·오류·선택지만 있는 턴처럼 최종 말풍선이
  //   아예 없는 경로가 있어서, 그때 그룹이 영원히 펼쳐진 채 남는다.
  const [liveTurnId, setLiveTurnId] = useState(0);
  const [chatBusyBlocked, setChatBusyBlocked] = useState(false);
  const chatAbortingRef = useRef(false);
  const [chatAborting, setChatAborting] = useState(false);
  const markChatAborting = (v) => { chatAbortingRef.current = v; setChatAborting(v); };
  // 이번 턴의 롤백 정보 { uid, txt } — 중지 시 이 uid 의 질문 말풍선을 지우고 txt 를 입력창에 되돌린다.
  const abortTurnRef = useRef(null);
  const markChatBusy = (v) => {
    chatBusyRef.current = v;
    setChatBusy(v);
    if (!v) { setChatBusyBlocked(false); setLiveTurnId(0); }
  };

  /*
    세션 id: 마운트(=앱 로드)마다 새로 발급한다. 세션은 사용자 전역(모듈 무관)이라
      모듈이 바뀌어도 같은 세션을 이어간다 — 백엔드가 요청마다 module 을 받는다.
      값은 불투명 32자 hex(newChatSessionId) — 서버 저장 형태와 같다.

    사용자 id 는 여기서 만들지 않는다. 예전에는 로그인 정보에서 뽑아 body 에 실었는데,
      로그인 응답(JwtResponse)에 사용자 id 필드가 없어 실제로는 항상 "anonymous" 였고
      서버가 인증에서 다시 채워 넣어 버려졌다. 클라이언트가 자기 신원을 주장하는 모양만
      남아 오해를 부르므로 없앴다 — user_id 의 유일한 출처는 서버 인증이다
      (ScenarioAiV1Controller.applyChatUser).
  */
  const chatSessionIdRef = useRef(newChatSessionId());
  // 세션 id 는 ref 라 바뀌어도 재렌더가 없다 — 히스토리 목록의 '현재 세션' 강조가 따라오도록
  //   표시용 state 를 함께 둔다(값은 항상 ref 가 정본).
  const [sessionId, setSessionIdState] = useState(chatSessionIdRef.current);
  const setSessionId = useCallback((sid) => {
    chatSessionIdRef.current = sid;
    setSessionIdState(sid);
  }, []);

  // ⚠️ 이번 턴을 **어느 모듈로 보냈는가**. 취소·피드백은 반드시 이 값으로 보내야 한다.
  //   백엔드 캐시 키(_agent_cache_key)에 module 이 들어가므로, 홈(GENERAL)에서 질문한 뒤
  //   시나리오 화면으로 이동해 중지를 누르면 현재 modCode(DP)로는 취소 대상을 찾지 못하고
  //   조용히 no-op 이 된다(UI 는 멈춘 것처럼 보이지만 서버 LLM 은 끝까지 돌고 과금된다).
  const turnModRef = useRef(modCode);

  // 이번 턴에 도구가 참고한 데이터 라벨({label, labelArgs}) — tool_done 이벤트에서 누적.
  const consultedRef = useRef([]);
  // 답변 피드백 — "sessionId:seq" → 'up'|'down'
  const [feedbackGiven, setFeedbackGiven] = useState({});

  // 되묻기 선택지 — 입력창 위 패널에 띄우는 현재 단계 {prompt, items[]}. 답하거나 건너뛰면 null.
  const [pendingChoices, setPendingChoices] = useState(null);
  const [choiceTrail, setChoiceTrail] = useState([]);
  const [choiceInput, setChoiceInput] = useState("");
  const choiceBusyRef = useRef(false);   // 연타 이중 실행 가드

  // SSE 델타 말풍선 진행 플래그
  const sseBubbleRef = useRef(false);

  // ── 뷰 신호 채널 ───────────────────────────────────────────────────────────
  //   draftSignal — 입력 초안 되돌리기/포커스. viewId=null 은 전 뷰 브로드캐스트.
  //   uiEpoch     — 뷰의 휘발성 UI(슬래시 팔레트 등)를 접어야 하는 시점.
  //   historyEpoch— 대화가 통째로 갈렸다(초기화·과거 대화 복원) → 뷰가 '즉시 스크롤'을 되찾는다.
  const [draftSignal, setDraftSignal] = useState(null);
  const [uiEpoch, setUiEpoch] = useState(0);
  const [historyEpoch, setHistoryEpoch] = useState(0);
  const draftEpochRef = useRef(0);
  const activeViewRef = useRef("panel");   // 마지막으로 명령을 보낸 뷰
  const emitDraft = useCallback((sig) => {
    draftEpochRef.current += 1;
    setDraftSignal({ epoch: draftEpochRef.current, viewId: activeViewRef.current, ...sig });
  }, []);
  /** 입력 포커스를 (명령을 보낸) 뷰로 돌려준다 — 선택지 패널이 사라진 뒤 대화가 끊기지 않게. */
  const focusChatInput = useCallback(() => emitDraft({ focus: true }), [emitDraft]);
  const bumpUi = useCallback(() => setUiEpoch(v => v + 1), []);

  // ── SSE 델타 말풍선 ────────────────────────────────────────────────────────
  //   서버가 토큰을 흘려보내면(chat/stream) 아래 3개로 말풍선을 직접 채운다.
  //   최종 확정은 typeMessage 가 맡는다 — sseBubbleRef 가 켜져 있으면 새 말풍선을 만들지 않고
  //   델타로 그리던 말풍선을 그 자리에서 최종본으로 덮는다.
  const beginSseBubble = useCallback(() => {
    sseBubbleRef.current = true;
    sseTailRef.current = "";
    streamedTurnRef.current = true;
    setMessages(p => [
      ...p.map(m => m.streaming ? { ...m, text: m._full ?? m.text, streaming: false } : m),
      // sseAt — 이 말풍선이 열린 시각. 예고 문장은 곧 도구가 흡수해 사라지고, 최종 답변은
      //   계속 흐른다. 스트리밍 중에 둘을 가릴 신호가 이것뿐이라 화면이 이 값으로 판단한다.
      { role: "a", text: "", _full: "", streaming: true, sse: true, sseAt: Date.now(),
        actions: [], expertCases: [], customDraft: null },
    ]);
  }, []);

  const appendSseDelta = useCallback((chunk) => {
    if (!chunk) return;
    sseTailRef.current += chunk;
    setMessages(p => {
      const next = [...p];
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].streaming) {
          const text = (next[i].text || "") + chunk;
          next[i] = { ...next[i], text, _full: text };
          break;
        }
      }
      return next;
    });
  }, []);

  // ── 도구 실행 구간 표시 ────────────────────────────────────────────────────
  //   델타가 흐르기 시작하면 typing 이 꺼지고, statusText 는 typing 말풍선 안에서만 그려진다
  //   (AgentChatMessageList). 그래서 "…확인하겠습니다" 뒤에 도구가 도는 수 초 동안 화면이
  //   커서만 깜빡이는 상태가 됐다(실측 7초). 그 구간에 도구 말풍선을 끼워 넣는다.
  const turnIdRef = useRef(0);
  const sseSegsRef = useRef(0);      // 이번 턴에 끊어 둔 본문 말풍선 수
  // 이번 턴에 델타가 한 글자라도 흘렀는가 — 최종 확정 때 타자기 애니메이션을 다시 돌릴지의 판정.
  //   이미 읽은 글을 처음부터 다시 찍으면 답이 사라졌다 되나오는 것으로 보인다.
  const streamedTurnRef = useRef(false);
  // 마지막으로 끊은 뒤 흘러온 본문 — 조각 배분이 어긋났을 때 **화면을 그대로 두기 위한** 폴백.
  //   통짜 fullText 를 마지막 말풍선에 넣으면 앞 말풍선들과 통째로 중복되므로 쓸 수 없다.
  const sseTailRef = useRef("");
  const toolRunningRef = useRef(0);  // 실행 중인 도구 수(한 라운드가 도구를 병렬로 부른다)
  // 이번 턴에 서버가 실제로 일을 시작했는가 — 스트림이 끊겼을 때 재전송해도 되는지의 판정.
  const turnStartedRef = useRef(false);
  const toolRunSeqRef = useRef(0);   // 도구 말풍선 고유 id (재정렬해도 안 흔들리는 렌더 key)

  /**
   * '도구 실행 중' 말풍선을 넣는다. 본문이 흐르는 중이었으면 그 말풍선을 먼저 끊는다.
   *
   * 한 라운드가 도구를 여러 개 부르면(예: 판매실적 조회 → 자유 조회) tool_start 가 연달아
   * 온다. 두 번째부터는 끊을 본문이 없으므로 **끊지 않고 말풍선만** 덧붙인다 — 여기서
   * 그냥 return 하면 뒤따르는 도구들이 통째로 안 보인다.
   */
  const appendToolRun = useCallback((labelKey, labelArgs, turnId, labelBase, toolName, retry,
                                     callId) => {
    const breaking = sseBubbleRef.current;
    // 판정은 ref 로 동기 처리한다 — setMessages 업데이터는 나중에 돌아서 반환값을 쓸 수 없다.
    if (breaking) {
      sseBubbleRef.current = false;   // 다음 델타가 새 말풍선을 연다
      sseSegsRef.current += 1;
      sseTailRef.current = "";        // 이 조각은 끊겼다 — 꼬리는 다음 말풍선 몫이다
    }
    setMessages(p => {
      const next = [...p];
      if (breaking) {
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].streaming) {
            const txt = next[i]._full ?? next[i].text ?? "";
            next[i] = { ...next[i], text: txt, _full: txt, streaming: false, segPart: true, turnId };
            break;
          }
        }
      }
      // id 는 렌더 key 용 — 아래에서 완료/진행 순으로 재정렬하므로 인덱스 key 를 쓰면
      //   말풍선이 통째로 remount 돼 점 애니메이션이 매번 처음부터 다시 돈다.
      toolRunSeqRef.current += 1;
      return [...next, { role: "a", turnId,
                         // retry 는 tool_start 에만 실린다 — 완료 시점에 새로 붙이면 칩 높이가
                         //   그때 변해 목록이 흔들린다(rowCount 와 반대로 처음부터 들고 간다).
                         toolRun: { id: toolRunSeqRef.current, key: labelKey, args: labelArgs,
                                    base: labelBase, tool: toolName, retry: retry || null,
                                    callId: callId || "", done: false, rowCount: -1 },
                         actions: [], expertCases: [], customDraft: null }];
    });
  }, []);

  /**
   * 진행 중 말풍선 하나를 완료 처리한다.
   *
   * ⚠️ 매칭 기준은 **호출 id 가 1순위**다. 한 라운드가 같은 도구를 병렬로 여러 번 부르면
   *   (챗 규칙은 한 답변에 차트 3장을 허용한다) 이름만으로는 어느 말풍선의 완료인지 알 수
   *   없어, 먼저 끝난 호출의 상태가 다른 호출의 칩에 붙는다.
   *   호출 id 가 없는 이벤트(도구 자체 emit·옛 말풍선)는 도구 이름으로 내려간다. 라벨 키로만
   *   맞추면 tool_start 와 tool_done 의 라벨이 다른 도구에서 말풍선이 영원히 진행 중으로
   *   남는다 — 실제로 rp_diagnose_root_cause 가 훅(chatToolRootCause)과 도구 자신
   *   (traceRpRootCause) 두 키를 써서 5개가 통째로 안 닫혔다.
   */
  const markToolRunDone = useCallback((labelKey, labelArgs, labelBase, rowCount, toolName,
                                      status, callId, retryable) => {
    const hits = (tr) => {
      if (callId && tr.callId) return tr.callId === callId;
      if (toolName && tr.tool) return tr.tool === toolName;
      if (labelKey && tr.key) return tr.key === labelKey || (labelBase && tr.base === labelBase);
      return true;   // 라벨도 이름도 없는 done — 가장 최근 미완료를 닫는다(구 동작)
    };
    setMessages(p => {
      const next = [...p];
      let hit = -1;
      for (let i = next.length - 1; i >= 0; i--) {
        if (next[i].toolRun && !next[i].toolRun.done && hits(next[i].toolRun)) {
          // 완료가 더 구체적인 라벨을 실어 오면 문구를 갱신한다 — 시작 시점에는 무엇을 볼지
          //   모르는 도구가 있다(자유 조회는 SQL 이 나와야 어느 데이터인지 안다).
          const upgraded = labelKey && labelKey !== next[i].toolRun.key
            ? { key: labelKey, args: labelArgs } : null;
          // status 는 실패도 완료로 말하지 않기 위한 것이다 — 도구가 사유 문장을 돌려준
          //   턴에 "…했습니다"로 닫히면 진행 표시가 본문과 정반대를 말한다.
          // retryable 은 "인자를 고쳐 다시 부르라"는 안내다 — 사용자 결과가 아니라 모델에게
          //   준 지시라, 칩으로 남기면 자기교정에 성공한 턴이 오류처럼 읽힌다(AgentChatToolGroup).
          next[i] = { ...next[i], toolRun: { ...next[i].toolRun, ...upgraded, done: true,
                                             status: status || "ok",
                                             retryable: !!retryable,
                                             rowCount: Number.isInteger(rowCount) ? rowCount : -1 } };
          hit = i;
          break;
        }
      }
      if (hit < 0) {
        // 이미 완료 처리한 말풍선에 done 이 한 번 더 온다 — 한 호출을 도구 자신과 훅이 각각
        //   기록하기 때문이다(RP 계열). **나중에 오는 것이 정본**이다: 백엔드가 두 라벨의
        //   권위를 정리한 결과를 싣고 오므로, 문구와 건수를 여기서 마저 반영한다.
        //   (이걸 안 하면 먼저 온 도구 자체 라벨 — 명사형 "보충 정책 구조 분석" — 이 굳는다.)
        const late = Number.isInteger(rowCount) && rowCount >= 0;
        const failed = status && status !== "ok";
        if (!late && !labelKey && !failed) return p;
        for (let i = next.length - 1; i >= 0; i--) {
          const tr = next[i].toolRun;
          if (!tr || !tr.done || !hits(tr)) continue;
          const patch = {};
          if (labelKey && labelKey !== tr.key) { patch.key = labelKey; patch.args = labelArgs; }
          if (late && !(tr.rowCount >= 0)) patch.rowCount = rowCount;
          if (failed && tr.status !== status) patch.status = status;
          if (retryable && !tr.retryable) patch.retryable = true;
          if (!Object.keys(patch).length) return p;
          next[i] = { ...next[i], toolRun: { ...tr, ...patch } };
          return next;
        }
        return p;
      }
      // 도구는 병렬로 돈다(strands ConcurrentToolExecutor) — 시작 순서와 완료 순서가 다르다.
      //   먼저 시작한 위쪽 말풍선이 아직 돌고 아래쪽에 체크가 먼저 뜨면 "위에 게 왜 안 끝나지"
      //   하는 오해를 준다. 진행 중인 것을 항상 맨 아래로 모아 '지금 하는 일'이 마지막에 오게 한다.
      let s = next.length;
      while (s > 0 && next[s - 1].toolRun) s -= 1;   // 꼬리의 연속 도구 말풍선 구간
      const grp = next.slice(s);
      if (grp.length < 2) return next;
      const done = grp.filter(m => m.toolRun.done);
      const running = grp.filter(m => !m.toolRun.done);
      if (!done.length || !running.length) return next;
      return [...next.slice(0, s), ...done, ...running];
    });
  }, []);

  /**
   * 최종 확정 직전, 끊어 둔 말풍선에 라운드 조각을 배분한다.
   * 반환값 = 마지막(현재 스트리밍) 말풍선에 넣을 본문.
   *
   * ⚠️ **끊어 둔 말풍선을 걷어내고 통짜 본문 하나로 되돌리지 않는다.** 어느 맥락에서 어떤 도구가
   * 돌았는지는 본문·도구 말풍선이 번갈아 놓인 순서 자체가 기록이라, 마지막에 하나로 합치면
   * 그 정보가 통째로 사라진다(실사고: 도구 칩 12개가 위로 몰리고 아래에 큰 말풍선 하나).
   * 조각 수가 안 맞으면(서버가 조각을 못 보낸 경우 등) **화면을 흘러온 그대로 둔다** — 마지막
   * 말풍선에는 그 구간에 흘러온 꼬리(sseTailRef)만 넣는다. 통짜 fullText 를 넣으면 앞 말풍선들과
   * 중복되므로 쓰지 않는다.
   * 서버가 걷어낸 조각은 빈 문자열로 온다(자리 유지 계약) — 그 말풍선만 지운다.
   */
  const applyReplyParts = useCallback((parts, kinds, fullText, turnId) => {
    const segs = sseSegsRef.current;
    if (!segs) return fullText;
    sseSegsRef.current = 0;
    const ok = Array.isArray(parts) && parts.length === segs + 1;
    // 배분을 포기하면 kinds 도 함께 버린다 — 조각과 짝이 안 맞는 성격표는 위험하다.
    if (!ok) return sseTailRef.current || fullText;
    const kindAt = (k) => (Array.isArray(kinds) && kinds.length === parts.length
      ? kinds[k] : null);
    setMessages(p => {
      const segIdx = p.reduce((a, m, i) => (m.segPart && m.turnId === turnId ? [...a, i] : a), []);
      if (!segIdx.length) return p;
      const next = [...p];
      const drop = new Set();
      segIdx.forEach((mi, k) => {
        const text = parts[k] || "";
        // answerPart = 사용자가 읽어야 할 본문 조각. 도구 이력으로 접지 않는다
        //   (AgentChatMessageList 의 그룹 판정). 예고 문장(lead)만 접힌다.
        if (text) next[mi] = { ...next[mi], text, _full: text,
                               answerPart: kindAt(k) === "body" };
        else drop.add(mi);   // 서버가 걷어낸 라운드(매달린 도입부 등) — 도구 말풍선은 남긴다
      });
      return drop.size ? next.filter((_m, i) => !drop.has(i)) : next;
    });
    return parts[segs];
  }, []);

  /** 델타 말풍선을 지운다 — 최종 응답에 표시할 본문이 없을 때(선택지 패널만 뜨는 턴). */
  const dropSseBubble = useCallback(() => {
    if (!sseBubbleRef.current) return;
    sseBubbleRef.current = false;
    setMessages(p => {
      const idx = p.map(m => !!m.streaming).lastIndexOf(true);
      return idx < 0 ? p : [...p.slice(0, idx), ...p.slice(idx + 1)];
    });
  }, []);

  // 타이핑 스트리밍 — extra 는 최종 말풍선에 병합할 부가 필드(consulted 칩, 피드백용 seq 등).
  const typeMessage = useCallback((fullText, actions = [], expertCases = [], customDraft = null, extra = null) => {
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    // SSE 델타로 이미 그리고 있던 말풍선이 있으면 다시 타이핑하지 않고 그 자리에서 확정한다.
    if (sseBubbleRef.current) {
      sseBubbleRef.current = false;
      setMessages(p => {
        const next = [...p];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].streaming) {
            next[i] = { ...next[i], text: fullText, _full: fullText, streaming: false,
                        actions, expertCases, customDraft, ...(extra || {}) };
            return next;
          }
        }
        // 델타가 하나도 안 온 턴(fast-path) — 그릴 말풍선이 없으니 새로 만든다.
        return [...next, { role: "a", text: fullText, _full: fullText, streaming: false,
                           actions, expertCases, customDraft, ...(extra || {}) }];
      });
      return;
    }
    // 이번 턴에 이미 델타가 흘렀는데 그릴 말풍선이 없다 = 도구가 마지막 본문 말풍선을
    //   끊어 간 경우다. 여기서 타자기를 다시 돌리면 방금 읽은 답이 빈 말풍선에서 처음부터
    //   다시 찍혀 "사라졌다 다시 나오는" 것으로 보인다 — 즉시 확정한다.
    if (streamedTurnRef.current) {
      setMessages(p => [
        ...p.map(m => m.streaming ? { ...m, text: m._full ?? m.text, streaming: false } : m),
        { role: "a", text: fullText, _full: fullText, streaming: false,
          actions, expertCases, customDraft, ...(extra || {}) },
      ]);
      return;
    }
    const CHUNK = 6;
    const DELAY = 14;
    let pos = 0;
    setMessages(p => [
      ...p.map(m => m.streaming ? { ...m, text: m._full ?? m.text, streaming: false } : m),
      { role: "a", text: "", _full: fullText, streaming: true, actions: [], expertCases: [], customDraft: null },
    ]);
    streamTimerRef.current = setInterval(() => {
      pos = Math.min(pos + CHUNK, fullText.length);
      const sliced = fullText.slice(0, pos);
      setMessages(p => {
        const next = [...p];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].streaming) { next[i] = { ...next[i], text: sliced }; break; }
        }
        return next;
      });
      if (pos >= fullText.length) {
        clearInterval(streamTimerRef.current);
        streamTimerRef.current = null;
        setMessages(p => p.map(m => m.streaming
          ? { ...m, streaming: false, actions, expertCases, customDraft, ...(extra || {}) } : m));
      }
    }, DELAY);
  }, []);

  // 진행 중인 SSE·타이머 정리 — 엔진은 앱 수명 동안 살아 있으므로 실제로는 앱 종료 시점에만 돈다.
  useEffect(() => () => {
    try { chatAbortRef.current?.abort(); } catch {}
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    if (statusTimerRef.current) clearInterval(statusTimerRef.current);
  }, []);

  // ── 화면(메뉴 탭) 이동 요청 ────────────────────────────────────────────────
  //   메뉴 지식은 App(plannel:navScreen 리스너)이 소유(순환 import 방지). dispatchEvent 는 동기라
  //   onResult 로 성공 여부·화면명이 즉시 돌아온다. req: {back}|{name}|{current}|{anchor}|{peek}
  const requestScreenNav = useCallback((req) => {
    let res = null;
    try {
      window.dispatchEvent(new CustomEvent("plannel:navScreen",
        { detail: { ...req, onResult: (r) => { res = r; } } }));
    } catch { /* 무시 — res=null 로 실패 처리 */ }
    return res;
  }, []);

  // ── 액션 레지스트리 ────────────────────────────────────────────────────────
  const registryActions = useAgentActions();
  // implRef — 아래에서 정의되는 명령들의 최신 구현. liveActions 의 run 클로저가 여기를 통해
  //   호출하므로 목록 identity 를 [registryActions, t] 로 고정할 수 있다.
  const implRef = useRef({});
  const liveActions = useMemo(() => ([
    ...registryActions.filter(
      a => (a.group ?? "action") === "action" && (a.enabled ? a.enabled() : true)
    ),
    // 이전 화면으로 — 챗을 연 채 다른 화면(탭)으로 이동했다가 돌아올 때.
    { id: "nav.back", i18nKey: "agentNavBackLabel", ko: "이전 화면으로",
      description: t("agentNavBackDesc"), chipHidden: true,
      run: () => implRef.current.navBack?.() },
    // 대화 초기화 — 터미널 clear 처럼 대화를 최초 진입 상태로 되돌린다.
    { id: "chat.clear", i18nKey: "clearChatLabel", ko: "대화 초기화",
      description: t("clearChatDesc"), chipHidden: true,
      run: () => implRef.current.resetChat?.() },
  ]), [registryActions, t]);

  // 액션 표시 라벨 — i18nKey 없는 동적 액션(scn.select.* 등)은 ko 를 그대로 사용.
  const actLabel = useCallback(
    (a) => (a.i18nKey ? t(a.i18nKey, { defaultValue: a.ko ?? "" }) : (a.ko ?? "")), [t]);

  // 새 상호작용이 발생하면 이전 메시지의 미소진 액션 버튼을 모두 정리한다(문맥이 이미 지났으므로).
  //   화면 링크는 정리 대상이 아니다.
  //   링크는 그 답변 말풍선에 붙어 어느 맥락의 조회 조건인지 스스로 드러낸다.
  //   누르지 않으면 아무것도 하지 않는 제안이라 지난 턴에 남아 있어도 화면을 건드리지 않는다.
  const spendInteractives = (list) =>
    list.map(m => (m.actions?.length
      ? { ...m, actions: m.actions.map(a => ({ ...a, spent: true })) }
      : m));

  // 레지스트리 액션 실행 — "채팅 메시지"로 표시하되 실행은 client-side 결정적(LLM 미경유).
  const runLiveAction = (a) => {
    const label = actLabel(a);
    setMessages(p => [...spendInteractives(p), { role: "u", text: label }]);
    typeMessage(a.confirmKo || t("MSG_Applied"), []);
    try {
      Promise.resolve(a.run?.()).catch(() => typeMessage(t("MSG_ActionRunError"), []));
    } catch {
      typeMessage(t("MSG_ActionRunError"), []);
    }
  };

  const navBack = () => {
    const r = requestScreenNav({ back: true });
    typeMessage(r?.ok ? t("agentNavMovedToScreen", { screen: r.title })
                      : t("agentNavNoScreenHistory"), []);
  };

  // 백엔드 screen_links → 말풍선 하단 칩 데이터.
  //   라이브 응답과 복원된 지난 대화가 같은 변환을 타게 하려고 훅 스코프에 둔다.
  //   존재·권한은 peek 으로 먼저 확인한다(peek 은 이동 없이 조회만) — 죽은 칩을 만들지 않는다.
  //   표시명은 앱이 준 title 로 채운다.
  //   백엔드 name 을 그대로 쓰면 저장 당시 언어에 박혀 언어를 바꿔도 따라오지 않는다.
  //   중복 제거 기준은 peek 이 돌려준 숫자 key 다.
  //   백엔드가 같은 화면을 숫자 key 와 reduxKey 로 각각 보내면 원문 기준으로는 서로 달라 보여 칩이 둘 난다.
  //   App 이 pending 을 돌려주면(권한 조회 전) 링크를 살린다.
  //   클릭 시 정식 이동이 권한을 다시 보고 forbidden 문구는 openScreenLink 가 이미 갖고 있다.
  const buildScreenLinks = (raw) => {
    const out = [];
    const seen = new Set();
    for (const l of Array.isArray(raw) ? raw : []) {
      const k = l?.key ?? l?.screen;
      if (k == null || seen.has(String(k))) continue;
      const peek = requestScreenNav({ peek: true, key: k });
      if (!peek?.ok) {
        // 버려진 링크는 사유와 함께 남긴다. 칩이 통째로 사라지는 현상을 사유별로 가려내는 유일한 단서다.
        debug("[screenLinks] drop", { key: k, reason: peek?.reason || "unresolved" });
        continue;
      }
      const dedupKey = String(peek.key ?? k);
      if (seen.has(dedupKey)) continue;
      seen.add(String(k));
      seen.add(dedupKey);
      out.push({
        id: `sl${out.length + 1}`, key: peek.key, screen: l.screen || null,
        title: peek.title, params: l.params || {},
      });
    }
    return out;
  };

  // 관련 화면 링크 클릭 — 대화의 조회 조건(품목·거점·기간)을 함께 넘겨 화면을 연다.
  //   안내 문구는 **App 이 알려준 사실로만** 고른다. 예전엔 applied=false 일 때 link.params
  //   유무로 "이미 열려 있다"를 추측했는데, 조건을 심을 프리셋이 없는 화면(RP_REVIEW 등)까지
  //   그 문구로 떨어져 **닫혀 있던 화면을 열고도 "이미 열려 있어요"** 라고 답했다(사용자 신고).
  //   탭이 열려 있었는지는 챗이 알 수 없다 — staleTab 은 App 만 판정할 수 있다.
  const openScreenLink = (link) => {
    const r = requestScreenNav({ key: link?.key ?? link?.screen, screen: link?.screen, params: link?.params });
    if (r?.ok) {
      typeMessage(r.applied ? t("agentLinkOpenedWithCondition", { screen: r.title })
                : r.staleTab ? t("agentLinkAlreadyOpenHint", { screen: r.title })
                : t("agentLinkOpened", { screen: r.title }), []);
      return;
    }
    typeMessage(r?.reason === "forbidden" ? t("agentNavScreenNoPermission")
                                          : t("agentNavScreenNotFound"), []);
  };

  // ── 되묻기 선택지(choices) ─────────────────────────────────────────────────
  // 지금까지의 모든 질문·선택을 한 건의 문답 메시지로 남긴다.
  const pushChoiceRecord = (answer, status) => {
    const pairs = choiceTrail.map(h => ({ q: h.q, a: h.a }));
    pairs.push({ q: pendingChoices?.prompt || t("choicesDefaultPrompt"), a: answer || null });
    setMessages(p => [...spendInteractives(p), { role: "qa", qa: pairs, s: status || null }]);
  };

  const clearChoices = () => { setPendingChoices(null); setChoiceTrail([]); setChoiceInput(""); };

  // 시나리오를 구성(추가·방식 선택)하는 선택지인가 — 중첩 단계(next)까지 훑는다.
  const isComposingItem = (it) => {
    if (it?.next && choicesCompose(it.next)) return true;
    const a = it?.action;
    if (!a) return false;
    const id = a.action === "dispatch" ? String(a.payload?.id || "") : "";
    return adapter.isComposingAction(a.action, id);
  };
  function choicesCompose(step) {
    return (step?.items || []).some(isComposingItem);
  }

  // 단계에 딸려 온 '이미 정해진 앞 단계' — 사용자가 고른 적 없으므로 되돌아갈 지점(step)이 없다.
  const preTrail = (stepObj) => (stepObj?.trail || []).map(h => ({ q: h.q, a: h.a, step: null }));
  // 선택지·액션 라벨 표시 — label_key(+label_args) 우선, 없으면 서버 원문(label).
  //   전송 발화(it.send || it.label)는 번역하지 않는다 — 기록·표시만 화면 언어로 그린다.
  const choiceLabel = (it) => keyedText(t, it?.label_key, it?.label, it?.label_args);
  const canGoBackChoice = choiceTrail.some(h => h.step);

  // 직전 단계로 — 연속 선택 중 잘못 고른 단계를 되돌린다.
  const goBackChoice = () => {
    let moved = false;
    setChoiceTrail(prev => {
      let i = prev.length - 1;
      while (i >= 0 && !prev[i].step) i--;
      if (i < 0) return prev;
      setPendingChoices(prev[i].step);
      moved = true;
      return prev.slice(0, i);
    });
    // 입력 단계에서 선택지 단계로 돌아가면 패널 입력칸이 사라진다 — 포커스를 채팅창으로.
    if (moved && pendingChoices?.input) focusChatInput();
    setChoiceInput("");
    choiceBusyRef.current = false;
  };

  const choiceInputValue = choiceInputValueOf(pendingChoices?.input, choiceInput);

  // 입력값 확정 — 선택지 클릭과 같은 경로로 흘려보내 실행·기록을 한 곳에서 처리한다.
  const submitChoiceInput = () => {
    const inp = pendingChoices?.input;
    if (!inp || !choiceInputValue) return;
    focusChatInput();   // 패널 입력칸이 사라지므로 포커스를 채팅창으로 되돌린다
    // 초안 파라미터 값 입력 — 서버로 보내지 않고 편집본에 반영한 뒤 목록으로 돌아간다.
    if (inp.localDraftKey) {
      applyDraftParam(inp.localDraftKey, choiceInputValue.value);
      return;
    }
    if (inp.kind === "text") {
      // send_template 이 있으면 "무엇을 하려고 쓴 답인지"를 함께 실어 보낸다.
      const send_ = inp.send_template
        ? String(inp.send_template).replace("{value}", choiceInputValue.value)
        : choiceInputValue.value;
      handleChoiceClick({ id: "input", label: choiceInputValue.display, send: send_ });
      return;
    }
    handleChoiceClick({
      id: "input", label: choiceInputValue.display, kind: "action",
      action: { action: "set_param",
                payload: { field: inp.field, value: choiceInputValue.value,
                           role: inp.role ?? null, displayValue: choiceInputValue.display } },
    });
  };

  // 실행 확인 문구에서 선택 내용을 되풀이하는 문장은 버리고 새 정보만 남긴다.
  const meaningfulStatus = (text, label) => {
    const raw = String(text || "").trim();
    if (!raw) return null;
    const norm = (s) => String(s || "").replace(/[\s'"'"‘’“”「」『』]/g, "").toLowerCase();
    const ci = String(label || "").lastIndexOf(":");
    const cores = [norm(label), ci >= 0 ? norm(String(label).slice(ci + 1)) : ""]
      .filter(c => c.length >= 2);
    if (!cores.length) return raw;
    const kept = raw.split(/(?<=[.!?。！？])\s+/)
      .filter(s => !cores.some(c => norm(s).includes(c)));
    return kept.length ? kept.join(" ") : null;
  };

  const dismissChoices = () => {
    if (pendingChoices?.items?.length || pendingChoices?.input) pushChoiceRecord(null);
    if (pendingChoices?.input) focusChatInput();
    // 대상 시나리오를 묻던 선택지를 건너뛰면 그 적용도 취소된다(대기 상태가 남지 않게).
    setPendingParam(null);
    clearChoices();
    choiceBusyRef.current = false;
  };

  // ── 커스텀 시나리오 초안 파라미터 수정 (선택지 안에서 로컬 처리) ─────────────
  const draftEditRef = useRef(null);   // { payload, meta, eid } — 편집 중인 초안

  const draftParamDisplay = (key, value) => {
    const m = draftEditRef.current?.meta?.[key] || {};
    const opt = (m.options || []).find(o => String(o.value) === String(value));
    const shown = opt ? (t(opt.label_key || opt.label) || String(opt.value)) : String(value ?? "");
    const unit = m.unit_key ? t(m.unit_key) : (m.unit || "");
    return unit ? `${shown} ${unit}` : shown;
  };
  const draftParamLabel = (key) => {
    const m = draftEditRef.current?.meta?.[key] || {};
    return t(m.label_key || m.label) || key;
  };

  // 1단계 — 등록/수정 되묻기(백엔드가 처음 띄운 것과 같은 모양을 로컬에서 재구성)
  const openDraftConfirm = () => {
    const d = draftEditRef.current;
    if (!d) return;
    setPendingChoices({
      prompt: t("customDraftConfirmPrompt"),
      items: [
        { id: "c1", label: t("customDraftRegisterAsIs"), kind: "action",
          action: { action: "save_custom_scenario", payload: d.payload } },
        { id: "c2", label: t("customDraftEditParams"), kind: "action",
          action: { action: "edit_custom_scenario_params", payload: d.payload } },
      ],
    });
    setChoiceTrail([]);
    choiceBusyRef.current = false;
  };

  // 2단계 — 어떤 파라미터를 고칠지
  const openDraftParamList = () => {
    const d = draftEditRef.current;
    if (!d) return;
    const items = Object.keys(d.payload.params || {}).map((key, i) => ({
      id: `p${i + 1}`, label: draftParamLabel(key),
      description: t("customDraftCurrentValue", { value: draftParamDisplay(key, d.payload.params[key]) }),
      kind: "action", action: { action: "draft_param_pick", payload: { key } },
    }));
    if (!items.length) { openDraftConfirm(); return; }
    items.push({ id: "done", label: t("customDraftEditDone"), kind: "action",
                 action: { action: "draft_param_done", payload: {} } });
    setPendingChoices({ prompt: t("customDraftPickParamPrompt"), items });
    setChoiceTrail([]);
    choiceBusyRef.current = false;
  };

  // 3단계 — 값 입력. select/toggle 은 옵션 선택지로, 그 외는 입력칸으로(기본값 = AI 제안값).
  const openDraftParamEdit = (key) => {
    const d = draftEditRef.current;
    if (!d) return;
    const m = d.meta?.[key] || {};
    const cur = d.payload.params?.[key];
    const opts = Array.isArray(m.options) ? m.options : [];
    if ((m.type === "select" || m.type === "toggle") && opts.length) {
      setPendingChoices({
        prompt: t("customDraftEnterValuePrompt", { label: draftParamLabel(key) }),
        items: opts.map((o, i) => ({
          id: `v${i + 1}`,
          label: (t(o.label_key || o.label) || String(o.value))
                 + (String(o.value) === String(cur) ? ` (${t("currentLabel")})` : ""),
          kind: "action", action: { action: "draft_param_set", payload: { key, value: o.value } },
        })),
      });
    } else {
      const range = (m.min != null || m.max != null) ? `${m.min ?? ""} ~ ${m.max ?? ""}` : "";
      setPendingChoices({
        prompt: t("customDraftEnterValuePrompt", { label: draftParamLabel(key) }),
        input: { kind: "text", label: draftParamLabel(key), localDraftKey: key,
                 placeholder: range || String(cur ?? "") },
      });
      setChoiceInput(String(cur ?? ""));   // AI 제안값을 기본값으로 — 그대로 두면 유지
    }
    setChoiceTrail([]);
    choiceBusyRef.current = false;
  };

  // 값 반영 — payload(저장 대상)와 말풍선 카드(표시)를 함께 갱신해 둘이 어긋나지 않게 한다.
  const applyDraftParam = (key, value) => {
    const d = draftEditRef.current;
    if (!d) return;
    d.payload = { ...d.payload, params: { ...(d.payload.params || {}), [key]: value } };
    setMessages(p => p.map(m => (m.customDraft?._eid && m.customDraft._eid === d.eid)
      ? { ...m, customDraft: { ...m.customDraft, params: {
          ...m.customDraft.params,
          [key]: { ...(m.customDraft.params?.[key] || {}), value, confidence: "" },
        } } }
      : m));
    openDraftParamList();
  };

  const handleChoiceClick = (it) => {
    // 같은 프레임 연타 가드 — setPendingChoices 반영 전 재클릭이 통과해 이중 실행되는 것을 막는다.
    if (choiceBusyRef.current) return;
    choiceBusyRef.current = true;
    // 다음 단계가 있으면 실행·기록 없이 이어간다 — 마지막 선택에서만 종합 말풍선이 나온다.
    if (it.next?.items?.length || it.next?.input) {
      setChoiceTrail(prev => [...prev,
        { q: pendingChoices?.prompt || "", a: choiceLabel(it), step: pendingChoices },
        ...preTrail(it.next)]);
      // 보낸 화면 표시는 다음 단계로 잇는다 — 입력형 단계(submitChoiceInput)도 이 값을 읽는다. [Q-131]
      setPendingChoices(pendingChoices?.originOwner
        ? { ...it.next, originOwner: pendingChoices.originOwner } : it.next);
      setChoiceInput("");
      choiceBusyRef.current = false;
      return;
    }
    const finish = (status) => { pushChoiceRecord(choiceLabel(it), status); clearChoices(); };
    // 텍스트 선택지: 문답 기록을 직접 남기므로 send 의 사용자 말풍선 에코는 끈다(silent).
    const sendFallback = () => { finish(); send(it.send || it.label, { trusted: true, silent: true }); };
    // 화면이 만든 선택지(대상 시나리오 선택 등) — 백엔드 디스크립터가 아니라 로컬 콜백으로 처리한다.
    if (typeof it.onClick === "function") {
      finish();
      try { it.onClick(); } catch { typeMessage(t("MSG_ActionRunError"), []); }
      return;
    }
    if (it.kind === "action" && it.action) {
      const a = it.action;
      // 커스텀 시나리오 초안 수정 — LLM 왕복 없이 화면 상태만 바꾸는 로컬 흐름.
      if (a.action === "edit_custom_scenario_params") {
        if (!draftEditRef.current) return;   // 초안 컨텍스트 없음(복원된 이전 대화 등)
        draftEditRef.current.payload = a.payload || draftEditRef.current.payload;
        openDraftParamList();
        return;
      }
      if (a.action === "draft_param_pick") { openDraftParamEdit(a.payload?.key); return; }
      if (a.action === "draft_param_set") { applyDraftParam(a.payload?.key, a.payload?.value); return; }
      if (a.action === "draft_param_done") { openDraftConfirm(); return; }
      // 메뉴 화면 이동 — 실행은 프론트 로컬(App 의 plannel:navScreen). 권한은 App 이 다시 확인한다.
      //   ⚠️ 이 분기가 없으면 클릭이 onExecuteAction 으로 흘러 sendFallback(LLM 재왕복)이 된다.
      if (a.action === ACTION.NAV_SCREEN) {
        finish();
        const p = a.payload || {};
        const r = requestScreenNav(p.key != null ? { key: p.key } : { name: p.name });
        if (r?.ok) { typeMessage(t("agentNavMovedToScreen", { screen: r.title }), []); return; }
        if (r?.reason === "forbidden") { typeMessage(t("agentNavScreenNoPermission"), []); return; }
        // 이름을 해석하지 못했다 — 백엔드가 카탈로그로 이미 교정하므로 여기까지 오는 일은 드물다(안전망).
        const cands = p.name ? (requestScreenNav({ search: p.name })?.items ?? []) : [];
        if (cands.length) {
          setPendingChoices({
            prompt: t("agentNavScreenPickPrompt"),
            items: cands.map((m) => ({
              id: `nav${m.key}`, label: t("agentNavMoveToScreenLabel", { screen: m.name }),
              kind: "action", action: { action: ACTION.NAV_SCREEN, payload: { key: m.key, name: m.name } },
            })),
          });
          choiceBusyRef.current = false;
          return;
        }
        typeMessage(t("agentNavScreenNotFound"), []);
        return;
      }
      // 수정 후 등록 — 편집된 값이 정본이므로 저장 payload 를 편집본으로 바꿔 넘긴다.
      if (a.action === "save_custom_scenario" && draftEditRef.current) {
        a.payload = draftEditRef.current.payload;
      }
      // 시나리오 추가 상한 가드 — executeAction 이 상한 도달 무시(no-op)를 true 로 삼켜
      //   거짓 성공 에코가 나는 것을 막는다. 이 경로는 패널을 닫지 않는다.
      const addsScenario = a.action === "select_template"
        || (a.action === "dispatch"
            && (a.payload?.id === "scn.add.custom" || String(a.payload?.id || "").startsWith("tpl.add.")));
      if (addsScenario && composeScenarioCount >= maxScenarios) {
        typeMessage(t("MSG_ScenarioLimitReached", { max: maxScenarios }), []);
        choiceBusyRef.current = false;
        return;
      }
      // 🔴 다른 화면에서 보낸 턴의 선택지(응답이 탭을 옮긴 뒤 도착해 여기 떴다) — 이 화면에 적용하지 않는다.
      //   role 이 있어도 마찬가지다(alt1·alt2 는 모듈마다 있는 이름이라 엉뚱한 카드에 맞는다). 보낸 화면
      //   주인으로 보관만 하고, 이 화면의 단계는 건드리지 않는다. 보낸 화면으로 돌아가 구성하면 적용된다. [Q-131]
      const choiceOwner = pendingChoices?.originOwner ?? ownerKey;
      if (a.action === "set_param" && choiceOwner !== ownerKey) {
        finish();
        setPendingParamApply({ field: a.payload?.field, value: a.payload?.value,
                               displayValue: a.payload?.displayValue, label: a.payload?.displayValue,
                               owner: choiceOwner });
        typeMessage(t("MSG_ParamHeldForOriginScreen"), []);
        return;
      }
      // 대상이 비어 있는 파라미터 변경 — 적용할 시나리오가 없으면 값만 보관하고 구성 단계로 안내한다.
      if (a.action === "set_param" && !a.payload?.role
          && editableScenarios().length === 0) {
        finish();
        setPendingParamApply({ field: a.payload?.field, value: a.payload?.value,
                               displayValue: a.payload?.displayValue, label: a.payload?.displayValue,
                               owner: ownerKey });
        try { onExecuteAction?.({ action: "navigate", payload: { step: 1 } }); } catch {}
        typeMessage(t("MSG_NoScenarioAutoApplyShort"), []);
        return;
      }
      if (a.action === "dispatch") {
        const act = liveActions.find(x => x.id === a.payload?.id);
        if (!act) { sendFallback(); return; }   // 레지스트리 stale 등 — LLM 왕복으로 처리
        finish(meaningfulStatus(act.confirmKo, choiceLabel(it)));
        try {
          Promise.resolve(act.run?.(a.payload?.args)).catch(() => typeMessage(t("MSG_ActionRunError"), []));
        } catch {
          typeMessage(t("MSG_ActionRunError"), []);
        }
        return;
      }
      let ok = false;
      try { ok = !!(onExecuteAction && onExecuteAction(a)); } catch { ok = false; }
      if (!ok) { sendFallback(); return; }
      finish();
      return;
    }
    sendFallback();
  };

  // ── 대화 초기화 / 세션 ─────────────────────────────────────────────────────
  // 화면 초기화 — 최초 진입 상태(웰컴 1건)로 되돌린다.
  //   ⚠️ 세션 id 는 건드리지 않는다 — "새 대화"(resetChat)와 "지난 대화 불러오기"(loadChatSession)가
  //     화면 정리는 공유하되 세션 id 는 정반대로 다루기 때문이다.
  const clearConversation = () => {
    chatGenRef.current += 1;   // 세대 증가 — 진행 중이던 send() 응답을 폐기 대상으로 표시
    try { chatAbortRef.current?.abort(); } catch {}   // 진행 중인 SSE 도 즉시 끊는다
    sseBubbleRef.current = false;
    if (streamTimerRef.current) { clearInterval(streamTimerRef.current); streamTimerRef.current = null; }
    if (statusTimerRef.current) { clearInterval(statusTimerRef.current); statusTimerRef.current = null; }
    setMessages([{ role: "a", text: initMsg, greeting: true }]);
    setTyping(false);
    setStatusText("");
    setPendingParam(null);
    setPendingParamApply(null);
    clearChoices();
    choiceBusyRef.current = false;
    // 진행 중이던 턴의 finally 도 곧 이걸 끄지만, 초기화 직후 바로 전송할 수 있어야 한다.
    markChatBusy(false);
    consultedRef.current = [];
    // 뷰 정리 — 입력 초안은 전 뷰에서 비우고(브로드캐스트), 슬래시 팔레트는 uiEpoch 로 접는다.
    draftEpochRef.current += 1;
    setDraftSignal({ epoch: draftEpochRef.current, viewId: null, clear: true });
    bumpUi();
    setHistoryEpoch(v => v + 1);
  };

  /*
    새 세션 발급 — 백엔드 대화 컨텍스트 이월을 차단한다(새 대화로 취급).

    id 는 여기서 끝난다. 예전에는 클라이언트 임시 id 를 발급한 뒤 서버(POST .../chat/sessions)가
    준 uuid 로 교체했는데, 그 교체가 "전송 중 아님 && 말풍선 1개 이하"일 때만 성립해 진입점마다
    승자가 갈렸다(홈 카드는 resetChat 직후 곧바로 send 하므로 항상 클라 id 가 남았다). 결국 DB 에
    두 형식이 섞였다. 서버 발급은 DB 행을 만들지도 않는 순수 난수 생성이라(세션 행은 첫 턴의
    save_turn UPSERT 가 만든다) 왕복을 유지할 이유가 없었다 — 프로덕션에서는 그 왕복이
    Java→AgentCore 콜드 스타트를 탄다.
  */
  const startNewSession = () => {
    setSessionId(newChatSessionId());
  };

  // 새 대화 — "/clear" 커맨드와 헤더 "새 채팅" 버튼의 동작.
  const resetChat = () => {
    clearConversation();
    startNewSession();
    invalidateSessions();
  };

  // ── 챗 히스토리(서버 세션) ─────────────────────────────────────────────────
  //   목록은 엔진이 캐시한다 — 패널 기록 버튼과 홈 카드가 각각 조회하면 같은 목록을 두 번 받는다.
  const [sessions, setSessions] = useState(null);   // null=미조회/로딩, []=없음
  // 이 화면에서 서버 삭제가 확정된 세션 id. 목록 응답을 받을 때마다 이 집합으로 거른다.
  // 삭제보다 먼저 출발한 목록 조회가 삭제 뒤에 도착하면 지운 행을 그대로 담고 있다.
  // 그 응답이 화면을 덮어쓰면 지운 대화가 되살아나고, 그 행을 열면 비활성 세션에 이어 쓰게 된다.
  const deletedSidsRef = useRef(new Set());
  const stripDeleted = useCallback((list) => {
    const dead = deletedSidsRef.current;
    return dead.size ? list.filter(s => !dead.has(s.session_id)) : list;
  }, []);
  // 지난 대화를 불러오는 중(서버 왕복) — 이 구간에는 화면에 아직 **이전 대화**가 남아 있다.
  const [loadingSession, setLoadingSession] = useState(false);
  const loadingSessionRef = useRef(false);
  const sessionsInflightRef = useRef(null);
  const sessionsLoadedRef = useRef(false);
  const invalidateSessions = useCallback(() => { sessionsLoadedRef.current = false; }, []);
  const refreshSessions = useCallback(({ force = false } = {}) => {
    if (!force && sessionsLoadedRef.current) return sessionsInflightRef.current ?? Promise.resolve(null);
    if (sessionsInflightRef.current) return sessionsInflightRef.current;
    setSessions(prev => (sessionsLoadedRef.current ? prev : null));
    // 세션은 사용자 전역 — 전 모듈 목록을 보여준다(행에 마지막 대화 모듈 표기).
    const p = agentChatTransport.sessionList("all")
      .then(r => {
        setSessions(stripDeleted(r?.sessions || []));
        sessionsLoadedRef.current = true;
        return r;
      })
      .catch(() => { setSessions([]); sessionsLoadedRef.current = true; return null; })
      .finally(() => { sessionsInflightRef.current = null; });
    sessionsInflightRef.current = p;
    return p;
  }, [stripDeleted]);

  /*
    지난 대화 불러오기.

    ⚠️ 서버 왕복이 끝난 **뒤에야** 화면을 갈아끼운다 — 실패했을 때 원래 대화를 잃지 않기 위해서다.
      그래서 그 사이 화면에는 직전까지 보던 대화가 그대로 남는데, 사용자는 이미 목록에서 하나를
      골랐으므로 "말풍선이 보이니 바뀌었구나"로 읽는다(엉뚱한 대화를 그 대화로 착각한다).
      그래서 왕복 구간을 loadingSession 으로 드러내고, 뷰는 그동안 목록 대신 로딩을 그린다.
      ref 를 함께 두는 이유는 send() 가 클로저라 state 로는 최신값을 못 보기 때문(chatBusyRef 와 동일).
  */
  const loadChatSession = async (sid) => {
    // 답변을 받는 중에 다른 뷰에서 기록을 열면 진행 중인 턴이 조용히 폐기된다 — 막는다.
    if (chatBusyRef.current || chatAbortingRef.current || loadingSessionRef.current) {
      setChatBusyBlocked(true);
      return;
    }
    loadingSessionRef.current = true;
    setLoadingSession(true);
    try {
      const data = await agentChatTransport.sessionMessages(modCode, sid);
      clearConversation();                      // 진행 중 턴 정리(세대 증가·SSE 절단 포함)
      //   ⚠️ resetChat 이 아니다 — 새 세션을 발급하면 그 늦은 응답이 아래 id 를 덮어써
      //     "화면은 불러온 과거 대화인데 다음 메시지는 새 세션으로 나가는" 상태가 된다.
      setSessionId(sid);
      const restored = hydrateRestoredMessages(t, data?.messages, stepNameKeys, buildScreenLinks);
      if (restored.length) {
        setMessages([{ role: "a", text: initMsg, greeting: true }, ...restored]);
      }
    } catch (e) {
      setMessages(p => [...p, { role: "sys",
                                text: chatErrorText(t, e) || t("agentChatLoadError") }]);
    } finally {
      loadingSessionRef.current = false;
      setLoadingSession(false);
    }
  };

  /*
    지난 대화 삭제(서버 소프트 삭제).

    서버가 성공을 확정한 뒤에만 목록에서 지운다.
    낙관적으로 먼저 지우면 실패 시 되돌릴 스냅샷이 필요하다.
    확인 모달이 왕복 동안 화면을 막고 있으므로 늦게 지워져도 사용자는 차이를 느끼지 못한다.
    성공 판정은 `deleted === true` 하나다.
    서버는 이미 지워진 세션도 deleted:true(already) 로 답한다.
    실패는 messages 에 넣지 않는다.
    messages 길이가 hasConversation 신호라, 넣으면 홈 카드가 채팅 뷰로 바뀐다.
    대신 결과를 돌려주고, 확인 모달이 그 자리에서 보여 준다.
    @returns {Promise<boolean>} 삭제 성공 여부
  */
  const deleteChatSession = async (sid) => {
    let r;
    try { r = await agentChatTransport.sessionDelete(modCode, sid); }
    catch { return false; }
    if (r?.deleted !== true) return false;
    deletedSidsRef.current.add(sid);
    setSessions(p => (p == null ? p : p.filter(s => s.session_id !== sid)));
    // 현재 보고 있는 세션을 지웠으면 새 대화로 — 삭제된 세션에 이어 쓰지 않는다.
    if (sid === chatSessionIdRef.current) resetChat();
    return true;
  };

  // ── 답변 피드백(up/down) — 저장된 턴(seq 보유)에만 노출된다 ──────────────────
  const sendFeedback = (msg, rating, reason = "") => {
    const sid = msg.sessionId || chatSessionIdRef.current;
    const key = `${sid}:${msg.seq}`;
    setFeedbackGiven(p => ({ ...p, [key]: rating }));
    agentChatTransport.feedback(turnModRef.current, {
      sessionId: sid, seq: msg.seq, rating, reason,
    }).catch(() => {});
  };

  // ── 답변 중지 ──────────────────────────────────────────────────────────────
  //   이번 턴을 "없던 일"로 되돌린다(질문 말풍선·부분 답변 제거 + 입력창 복원).
  //   ⚠️ SSE fetch 를 끊는 것만으로는 서버가 멈추지 않는다(워커 스레드 전용 이벤트 루프).
  //      실제 취소는 chatAbort 요청이 담당한다 — 그리고 **이번 턴을 보낸 모듈**로 보내야 한다.
  const abortChat = () => {
    if (!chatBusyRef.current) return;
    const turn = abortTurnRef.current;
    abortTurnRef.current = null;

    // ① 서버 취소를 먼저 쏜다 — fetch 를 먼저 끊으면 Java 워커가 앞서 회수되며 로그 순서가 뒤집힌다.
    markChatAborting(true);
    agentChatTransport
      .abort(turnModRef.current, { sessionId: chatSessionIdRef.current })
      .catch(() => {})
      .finally(() => markChatAborting(false));

    // ② 세대 증가 — in-flight send() 의 잔여 렌더를 원천 차단한다.
    //    ⚠️ 세대가 바뀌면 send() 의 finally(markChatBusy) 와 catch(_stopStatus) 가 모두 건너뛰어진다.
    chatGenRef.current += 1;
    try { chatAbortRef.current?.abort(); } catch {}
    if (streamTimerRef.current) { clearInterval(streamTimerRef.current); streamTimerRef.current = null; }
    if (statusTimerRef.current) { clearInterval(statusTimerRef.current); statusTimerRef.current = null; }
    setStatusText("");
    setTyping(false);

    // ③ 롤백 — 질문 말풍선·받다 만 답변 제거 + 중지 안내 한 줄.
    setMessages(p => {
      let next = turn?.uid ? p.filter(m => m._uid !== turn.uid) : p;
      const idx = next.map(m => !!m.streaming).lastIndexOf(true);
      if (idx >= 0) next = [...next.slice(0, idx), ...next.slice(idx + 1)];
      return [...next, { role: "sys", text: t("chatStoppedNotice") }];
    });
    sseBubbleRef.current = false;
    markChatBusy(false);
    // 입력 복원 + 포커스는 **보낸 뷰**로만 — 숨겨진 패널에 포커스가 빨리지 않게 한다.
    //   중지를 누른 뷰(activeViewRef)가 아니라 턴을 시작한 뷰다(위 abortTurnRef 주석 참조).
    emitDraft({
      text: turn?.txt || undefined,
      focus: true,
      viewId: turn?.viewId ?? activeViewRef.current,
    });
  };

  // ── Action 칩 set_param: 대상 시나리오 재확인 후 적용 ───────────────────────
  const [pendingParam, setPendingParam] = useState(null);
  // 지연 적용 — 제안 시점에 편집 시나리오가 없으면 파라미터를 보관했다가 시나리오가 생기면 자동 적용.
  const [pendingParamApply, setPendingParamApply] = useState(null);
  // 후보 표시명 — 화면 카드 라벨(예 "시나리오 1") + 시나리오명. 화면과 호칭 일치.
  const scnDisplay = (c) => {
    const lbl = c?.label;
    const nm = c?.name;
    if (lbl && nm && lbl !== nm) return `${lbl} · ${nm}`;
    return lbl || nm || c?.role || "";
  };
  // 파라미터를 적용할 수 있는 시나리오 후보 — **단일 기준**. base(현행 유지·잠금)와 비활성 변형 제외.
  const editableScenarios = () => composeStarted
    ? (scenarios || []).filter(s => s.role !== "base" && s.enabled !== false)
    : [];
  const applyParamToRole = (p, role, disp) => {
    setPendingParam(null);
    let ok = false;
    try {
      ok = onExecuteAction
        ? !!onExecuteAction({ action: "set_param", payload: { field: p.field, value: p.value, role, displayValue: p.displayValue, op: p.op } })
        : false;
    } catch { ok = false; }
    const where = role === "all" ? t("allScenariosDativeLabel") : (disp ? t("targetDativeLabel", { disp }) : "");
    typeMessage(
      ok ? t("MSG_ChangedTo", { where, value: p.displayValue })
         : t("MSG_ApplyFailedNoScenario"),
      [],
    );
    // 대상 선택지 버튼이 사라지면 포커스가 body 로 떨어진다 — 대화가 끊기지 않게 되돌린다.
    focusChatInput();
  };
  const requestParamTarget = (a, { skipEcho = false } = {}) => {
    const p = a.param;
    const label = a.i18nKey ? t(a.i18nKey, { defaultValue: a.ko ?? "" }) : (a.ko ?? "");
    if (!skipEcho) {
      setMessages(prev => [...spendInteractives(prev), { role: "u", text: label }]);
    }
    const candidates = editableScenarios()
      .map(s => ({ role: s.role, name: s.name, label: s.label }));
    if (candidates.length === 0) {
      // 적용할 시나리오가 아직 없음 → 지연 적용으로 보관 + 시나리오 구성(step 1)으로 안내.
      setPendingParamApply({ field: p.field, value: p.value, displayValue: p.displayValue, label, op: p.op, owner: ownerKey });
      if (onExecuteAction) { try { onExecuteAction({ action: "navigate", payload: { step: 1 } }); } catch {} }
      typeMessage(t("MSG_NoScenarioAutoApplyFull", { label }), []);
      return;
    }
    if (candidates.length === 1) {
      applyParamToRole(p, candidates[0].role, scnDisplay(candidates[0]));
      return;
    }
    // 대상이 둘 이상 — 버튼이 아니라 선택지 패널로 묻는다(질문·답이 한 말풍선으로 남는다).
    setPendingParam({ ...p, label, candidates });
    setPendingChoices({
      prompt: t("MSG_WhichScenarioToApply", { label }),
      items: [
        ...candidates.map((c, i) => ({
          id: `tgt${i + 1}`, label: scnDisplay(c),
          onClick: () => applyParamToRole(p, c.role, scnDisplay(c)),
        })),
        ...(candidates.length >= 2 ? [{
          id: "tgtAll", label: t("agentApplyAll", { defaultValue: "전체 적용 (둘 다)" }),
          onClick: () => applyParamToRole(p, "all", null),
        }] : []),
      ],
    });
    setChoiceTrail([]);
    choiceBusyRef.current = false;
  };

  // 지연 적용 — 보관된 파라미터가 있고 편집 가능한 시나리오가 생기면 자동 적용한다.
  //   🔴 보관한 화면에서만 소비한다 — 챗 엔진은 앱 셸에 하나라 보관값이 화면 전환을 넘어 살아남는다.
  //   예전에는 BF 에서 보관한 「외부 인자」 가 FP 카드에 「변경했습니다」 로 떨어졌다(캡처 ai_scenario_138).
  //   주인이 아니면 소비만 건너뛰고 보관은 유지한다 — 주인 화면으로 돌아가면 약속대로 적용된다. [Q-131]
  useEffect(() => {
    if (!pendingParamApply) return;
    if (!isPendingOwner(pendingParamApply, ownerKey)) return;
    if (editableScenarios().length === 0) return;
    const p = pendingParamApply;
    setPendingParamApply(null);
    requestParamTarget(
      { ko: p.label || t("paramChangeLabel"), param: { field: p.field, value: p.value, displayValue: p.displayValue, op: p.op } },
      { skipEcho: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarios, pendingParamApply, composeStarted, ownerKey]);

  // ── 팔레트/칩 3단계 dispatch ───────────────────────────────────────────────
  //   needsTarget(set_param 프리셋) → 대상 시나리오 재확인 / promptText → LLM 경유 전송 /
  //   그 외 → 결정형 즉시 실행. 슬래시 팔레트와 칩이 같은 규칙을 공유한다.
  const runPaletteAction = (a) => {
    if (!a) return { blocked: true };
    // 중지 처리 중에는 LLM 경유 항목만 막는다 — 결정형 항목은 세션 락과 무관하므로 통과.
    if (chatAbortingRef.current && a.promptText && !a.needsTarget) {
      setChatBusyBlocked(true);
      return { blocked: true };
    }
    if (a.id === "chat.clear") { resetChat(); return { accepted: true }; }
    if (a.id === "nav.back") { navBack(); return { accepted: true }; }
    if (a.needsTarget) requestParamTarget(a);
    // 전송문과 이력 표시를 분리 — 사용자가 한 행동은 "항목을 고른 것"이므로 말풍선엔 항목 이름을 남긴다.
    else if (a.promptText) send(a.promptText, { echo: actLabel(a) });
    else runLiveAction(a);
    return { accepted: true };
  };

  /** 뷰가 "지금은 보낼 수 없다"를 강조하고 싶을 때(입력창 안내 문구 강조). */
  const notifyBlocked = () => setChatBusyBlocked(true);

  // ── 전송 ───────────────────────────────────────────────────────────────────
  const NOT_SUPPORTED_REPLY = t("MSG_ChatNotSupportedApology");

  /**
   * 챗 한 턴을 SSE 로 받아 **최종 result 를 반환**한다.
   * 반환 형태가 비스트리밍 chat() 과 동일하므로 아래 응답 후처리 코드는 그대로 쓴다.
   */
  const sendViaStream = async (ctx, txt, gen, stopStatus) => {
    const session = { sessionId: chatSessionIdRef.current };
    const ac = new AbortController();
    chatAbortRef.current = ac;
    consultedRef.current = [];   // 이번 턴의 '참고한 데이터' 칩 누적 초기화
    const turnId = ++turnIdRef.current;
    setLiveTurnId(turnId);
    turnStartedRef.current = false;
    sseSegsRef.current = 0;
    streamedTurnRef.current = false;
    toolRunningRef.current = 0;
    let result = null;
    let errMsg = "";
    let acc = "";   // 델타 누적 — 중간 끊김 시 받은 만큼이라도 살린다
    try {
      await agentChatTransport.stream(turnModRef.current, ctx, txt, session, {
        signal: ac.signal,
        onEvent: (evt) => {
          if (chatGenRef.current !== gen) return;   // /clear 로 초기화된 대화 — 폐기
          if (evt.type === "delta") {
            // 첫 델타 = 답변이 보이기 시작하는 시점 — 진행 표시(의사 단계·타이핑 점)를 걷어낸다.
            if (!sseBubbleRef.current) { stopStatus(); setTyping(false); beginSseBubble(); }
            acc += evt.text || "";
            appendSseDelta(evt.text || "");
          } else if (evt.type === "thinking") {
            // 모델이 추론만 하고 있는 구간 — 도구도 텍스트도 없어 화면이 무음이 되던 자리다
            //   (실측 72.9초 턴에서 최종 답변 라운드 하나가 29.7초 동안 완전 무반응).
            //   본문 델타가 아니므로 말풍선에 쌓지 않고 진행 표시 한 줄로만 흘려보낸다.
            if (!sseBubbleRef.current) {
              if (evt.text) { stopStatus(); setStatusText(evt.text); }
              else setStatusText("");
            }
          } else if (evt.type === "tool_start" || evt.type === "tool_done") {
            const label = traceLabel(t, evt.label, evt.label_args);
            if (label) { stopStatus(); setStatusText(label); }
            // 도구가 돌 때마다 말풍선을 하나씩 남긴다 — **첫 델타 이전에도 남긴다.**
            //   예전에는 본문이 흐른 뒤에만 말풍선을 만들고 그 전에는 typing 말풍선의
            //   statusText 한 줄로만 보여줬다. 그런데 LLM 은 첫 라운드에서 텍스트 없이 도구만
            //   부르는 일이 흔하다(실측: 4개를 먼저 돌리고 그다음 라운드에서야 첫 문장을 썼다).
            //   그 4건은 한 줄을 서로 덮어쓰며 지나가 이력에 아무 흔적도 남지 않았다 —
            //   "조회하고 있다더니 완료 말풍선 없이 다음 동작으로 넘어간다"의 정체다.
            if (evt.type === "tool_start") {
              toolRunningRef.current += 1;
              turnStartedRef.current = true;   // 서버 턴이 살아 있다 — 재전송 금지
              setTyping(false);   // 대기 말풍선은 걷고 도구 말풍선으로 교체한다
              appendToolRun(evt.label, evt.label_args, turnId, evt.label_base, evt.tool, evt.retry,
                            evt.call_id);
            } else {
              toolRunningRef.current = Math.max(0, toolRunningRef.current - 1);
              markToolRunDone(evt.label, evt.label_args, evt.label_base, evt.row_count, evt.tool,
                              evt.status, evt.call_id, evt.retryable);
              // 도구가 다 끝났는데 다음 본문까지 또 텀이 있다 — 최종 답변 라운드가 그 자체로
              //   길다(실측 23.8초). 그동안 화면이 다시 비므로 대기 말풍선을 되살린다.
              //   statusText 를 도구 라벨 그대로 두면 끝난 조회를 계속 하는 것처럼 보인다.
              // ⚠️ 문구로 '답변'을 약속하지 않는다 — 이 구간 다음이 본답변이라는 보장이 없고
              //    실제로 조회 → 검토 → 또 조회를 반복한다. 방금 얻은 것을 보고 있다는 사실만 쓴다.
              if (toolRunningRef.current === 0 && !sseBubbleRef.current) {
                setStatusText(t("statusFindingsReviewing"));
                setTyping(true);
              }
            }
            // 칩은 **기준 키**로 모은다 — 상세 키(chatToolRiskOf 등)를 그대로 넣으면
            //   `${label}Noun` 명사형이 없어 진행형 긴 문장이 칩에 박히고, 인자만 다른 같은
            //   성격의 조회가 칩을 여러 개로 쪼갠다.
            const chipLabel = evt.label_base || evt.label;
            if (evt.type === "tool_done" && chipLabel
                && !CONSULTED_EXCLUDE_LABELS.has(chipLabel)
                && !consultedRef.current.some(c => c.label === chipLabel)) {
              consultedRef.current.push({ label: chipLabel });
            }
          } else if (evt.type === "result") {
            result = evt;
          } else if (evt.type === "error") {
            errMsg = evt.content || "";
          }
        },
      });
    } catch (e) {
      if (ac.signal.aborted) throw e;
      if (!sseBubbleRef.current && !result && !turnStartedRef.current) {
        return await agentChatTransport.send(turnModRef.current, ctx, txt, session);
      }
      throw e;
    } finally {
      // 자기 컨트롤러일 때만 비운다 — 뒤에 시작된 스트림이 있는데 먼저 끝난 쪽이 지우면
      //   /clear·언마운트가 살아 있는 스트림을 끊지 못한다.
      if (chatAbortRef.current === ac) chatAbortRef.current = null;
    }
    if (result) return result;
    // result 없이 스트림이 조용히 끝났다 = 중간 끊김(LB·프록시 유휴 타임아웃 등).
    // ⚠️ 서버 턴이 **이미 시작됐으면 다시 보내지 않는다.** 스트림만 끊겼을 뿐 서버는 계속
    //    돌고 있어서, 재전송은 같은 세션의 Agent 락에 걸려 "이전 질문에 답변하는 중입니다"
    //    말풍선만 남긴다(실사고: 도구 13개가 돈 뒤 스트림이 끊겨 자동 재전송 → 거절).
    //    아무 일도 시작되지 않은 경우(도구·델타 모두 없음)에만 비스트리밍으로 다시 받는다.
    if (!sseBubbleRef.current && !turnStartedRef.current) {
      return await agentChatTransport.send(turnModRef.current, ctx, txt, session);
    }
    // 이미 델타를 그린 뒤라면 회수가 불가능하다 — 받은 부분을 살리고 중단 사실만 알린다.
    const truncated = new Error(errMsg || "chat stream truncated");
    truncated.code = "STREAM_TRUNCATED";
    truncated.partial = acc;
    throw truncated;
  };

  /**
   * 한 턴 전송. **입력창은 건드리지 않는다** — 뷰가 낙관적으로 비우고,
   * 반환값이 { blocked: true } 면 되돌린다(뷰마다 초안이 따로이기 때문).
   */
  const send = async (text, opts = {}) => {
    const txt = String(text ?? "").trim();
    if (!txt) return { blocked: true };
    // 화면이 들고 있는 무거운 컨텍스트는 여기서 딱 한 번 당겨 읽는다(pull 채널).
    const live = getLive?.() ?? {};
    // 이전 턴이 아직 서버에서 처리 중 — 보내면 백엔드가 같은 Agent 인스턴스에 동시 진입해 거절당한다.
    //   ⚠️ 선택지 패널을 건드리기 전에 반환해야 차단된 전송이 되묻기를 지워버리지 않는다.
    //   지난 대화를 불러오는 중에도 막는다 — 지금 보내면 **곧 교체될 세션**으로 나가서,
    //   방금 고른 대화에는 없는 턴이 되고 화면에서도 사라진다.
    if (chatBusyRef.current || chatAbortingRef.current || loadingSessionRef.current) {
      setChatBusyBlocked(true);
      focusChatInput();
      return { blocked: true };
    }
    // 지금 보고 있는 화면 — 화면명과 업무 영역을 같은 응답에서 뽑는다(동기 조회 1회).
    const curNav = requestScreenNav({ current: true });
    // 이번 턴을 보낼 챗 모듈 = 화면의 업무 영역. 시나리오 화면은 자기 코드를 발행하고
    //   (screen.modCode), 그 밖의 화면은 메뉴 계층에서 파생한다. 어느 쪽도 아니면 업무
    //   영역을 모르는 화면이다.
    //   ⚠️ 렌더 시점이 아니라 여기서 정한다 — 탭을 옮긴 직후에도 실제로 보고 있는 화면과
    //      라우트가 어긋나지 않는다.
    const chatModule = screen?.modCode || resolveChatModule(curNav) || GENERAL_MOD_CODE;
    // 이번 턴을 보낸 모듈을 고정 — 취소·피드백이 이 값을 쓴다(화면 이동 후 중지 무력화 방지).
    turnModRef.current = chatModule;
    // 새 발화가 들어오면 답하지 않은 되묻기 선택지는 문맥이 지난 것 — 패널을 닫고 기록만 남긴다.
    if ((pendingChoices?.items?.length || pendingChoices?.input) && !opts.silent) pushChoiceRecord(null);
    clearChoices();
    bumpUi();   // 뷰의 슬래시 팔레트 등 휘발성 UI 접기

    // 백엔드 라우팅 미지원 모듈(navbar 전역 챗봇 등) — API 호출 없이 안내만 응답.
    if (!SUPPORTED_CHAT_MODULES.includes(chatModule)) {
      setMessages(p => [...spendInteractives(p), { role: "u", text: txt }]);
      typeMessage(NOT_SUPPORTED_REPLY, []);
      return { accepted: true };
    }

    // 명시적 '단계 이동' 명령 — 결정형 처리(상대: 다음/이전). 오탐 방지: 메시지 전체가 이동어일 때만.
    if (onExecuteAction && typeof currentStep === "number" && stepNameKeys.length > 0) {
      const w = txt.trim().replace(/[\s.!?~]+/g, "");
      const delta = /^(다음|다음단계|다음스텝|다음화면|다음으로|다음단계로|다음스텝으로|다음화면으로)$/.test(w) ? 1
                  : /^(이전|이전단계|이전스텝|이전화면|이전으로|이전단계로|뒤로|뒤로가기)$/.test(w) ? -1
                  : 0;
      if (delta !== 0) {
        const target = Math.max(0, Math.min(stepNameKeys.length - 1, currentStep + delta));
        setMessages(p => [...spendInteractives(p), { role: "u", text: txt }]);
        if (target === currentStep) {
          typeMessage(delta > 0 ? t("agentNavAlreadyLast") : t("agentNavAlreadyFirst"), []);
        } else {
          try { onExecuteAction({ action: "navigate", payload: { step: target } }); } catch {}
          typeMessage(t("agentNavMovedToStep", { step: t(stepNameKeys[target]) }), []);
        }
        return { accepted: true };
      }
    }

    // 화면(메뉴 탭) 이동 — 결정형 fast-path(LLM 미경유).
    {
      const one = txt.trim().replace(/\s+/g, " ").replace(/[.!?~]+$/, "");
      const isBack =
        /^((이전|직전|원래|하던) ?(화면|메뉴|탭)(으로)? ?)?(되?돌아가)(줘|기|자)?$/.test(one)
        || /^(이전|직전) ?(메뉴|탭)(으로|로)?$/.test(one);
      const nameM = !isBack && one.match(/^(.{1,30}?) ?(?:화면|메뉴|탭)? ?(?:으로|로) (?:이동|이동해줘|가줘|가자|열어줘|돌아가줘|돌아가)$/);
      if (isBack || nameM) {
        const r = requestScreenNav(isBack ? { back: true } : { name: nameM[1] });
        // 권한 밖 화면은 이름이 해석돼도 열리지 않는다 — LLM 폴백으로 넘기지 말고 여기서 알린다.
        if (r?.reason === "forbidden") {
          setMessages(p => [...spendInteractives(p), { role: "u", text: txt }]);
          typeMessage(t("agentNavScreenNoPermission"), []);
          return { accepted: true };
        }
        if (r?.ok || isBack) {
          setMessages(p => [...spendInteractives(p), { role: "u", text: txt }]);
          typeMessage(r?.ok ? t("agentNavMovedToScreen", { screen: r.title })
                            : t("agentNavNoScreenHistory"), []);
          return { accepted: true };
        }
        // 이름 미해석 → 폴백(계속 진행)
      }
    }

    // Action 탭 되돌리기 명령 — 결정형 안전장치(LLM 미준수 대비). 변경 내역이 있을 때만.
    if (!pendingParam) {
      const revert = /(적용하지\s?마|적용하지\s?말|되돌려|되돌리|원래대로|원상\s?복구|롤백|변경.{0,3}취소)/.test(txt);
      const hasChanges = live.currentParams && Object.keys(live.currentParams).length > 0;
      if (revert && onExecuteAction) {
        setMessages(p => [...spendInteractives(p), { role: "u", text: txt }]);
        let ok = false;
        if (hasChanges) { try { ok = !!onExecuteAction({ action: "reset_scenario", payload: { role: "all" } }); } catch { ok = false; } }
        typeMessage(ok ? t("MSG_AllChangesReverted") : t("MSG_NoChangesToRevert"), []);
        return { accepted: true };
      }
    }

    // 기존 액션 버튼·미응답 선택지를 비활성화(spent) + 사용자 메시지 추가 (silent 재전송은 echo 생략)
    //   _uid — 중지 롤백에서 이 말풍선만 정확히 지우기 위한 턴 식별자.
    if (!opts.silent) {
      const echoText = opts.echo || txt;
      const uid = `u${chatGenRef.current}-${Date.now().toString(36)}`;
      // 이번 턴을 **어느 뷰에서 보냈는지**도 함께 굳힌다. 중지 버튼은 두 뷰 모두에 있고
      //   chatBusy 가 전역 공유값이라, 패널에서 보낸 턴을 홈 카드에서 멈출 수 있다.
      //   그때 롤백된 입력이 '멈춘 뷰'로 가면 정작 보낸 쪽 입력창은 빈 채로 남는다.
      abortTurnRef.current = { uid, txt, viewId: activeViewRef.current };
      setMessages(p => [...spendInteractives(p), { role: "u", text: echoText, _uid: uid }]);
    } else {
      abortTurnRef.current = null;
    }

    setTyping(true);
    streamedTurnRef.current = false;
    // 여기부터가 서버 왕복 구간 — 끝날 때까지(finally) 추가 전송을 막는다.
    markChatBusy(true);
    const gen = chatGenRef.current;
    // 진행 상태 표시 (의사 단계). 실제 tool 단위 진행은 SSE 가 덮어쓴다.
    const stSteps = STATUS_STEPS.action;
    let _si = 0; setStatusText(t(stSteps[0]));
    if (statusTimerRef.current) clearInterval(statusTimerRef.current);
    statusTimerRef.current = setInterval(() => {
      _si = Math.min(_si + 1, stSteps.length - 1);
      setStatusText(t(stSteps[_si]));
    }, 1800);
    const _stopStatus = () => {
      if (statusTimerRef.current) { clearInterval(statusTimerRef.current); statusTimerRef.current = null; }
      setStatusText("");
    };
    try {
      const adapterCtx = adapter.buildContext?.() ?? {};
      // 비시나리오 화면의 모든 슬롯 버전과 화면 조건 — 칩과 같은 함수·같은 입력으로 만든다.
      //   시나리오 화면은 자기 버전 블록이 있어 싣지 않는다(백엔드도 같은 조건으로 거른다).
      //   조건은 화면 컴포넌트의 viewName 으로 찾는다. 홈은 둘 다 없어 조건이 비어 나간다.
      const screenCtx = !screen?.modCode ? {
        ...screenVersionsPayload(),
        ...screenConditionsPayload(buildScreenConditions(
          pickConditionRaw(store.getState().viewStates?.viewInfos, curNav?.viewName ?? curNav?.reduxKey),
          { t, versionNames: getScreenVersionsSnapshot().map((v) => v.name) })),
      } : {};
      const res = await sendViaStream({
        // ── 봉투(챗 소유) — 어느 화면에서 쓰든 의미가 같은 필드만 여기 둔다 ──────────────
        tab:          "action",
        current_step: currentStep,
        step_label:   stepLabel,
        action_hint:  opts.actionHint ?? null,
        // 화면이 선언한 현재 가능한 액션 목록 — 백엔드 LLM 이 이 중에서 id 를 고른다.
        available_actions: liveActions.filter(a => !a.clickOnly).map(a => ({
          id: a.id, label: actLabel(a), description: a.description ?? "",
          ...(a.name ? { name: a.name } : {}),
          ...(a.confirmKo ? { confirm: a.confirmKo } : {}),
        })),
        is_follow_up: (messagesRef.current || []).some(m => m.role === "u"),
        // 사용자가 **지금 보고 있는 화면** — 메뉴 지식은 App 이 갖고 있어 이벤트로 동기 조회한다.
        // ⚠️ is_scenario_planning 을 lv2 키 리터럴로 판정하지 않는다 — 시나리오 화면이
        //   AI 대메뉴 밖에도 있다(FP=스케줄 시나리오는 생산계획 > FP_SCENARIO_PLANNING, 2026-09-08).
        //   판정 재료는 아래 scenario_planning 과 **같은 것**(화면이 자기 모듈 코드를 발행했는가)을 쓴다.
        // reduxKey 는 백엔드가 지금 보고 있는 화면을 관련 화면 링크에서 빼는 식별자다.
        //   홈은 메뉴 트리 밖이라 null 이다.
        current_screen: curNav?.ok
          ? { title: curNav.title, path: curNav.path,
              reduxKey: curNav.reduxKey || null,
              is_scenario_planning: !!screen?.modCode }
          : null,
        // 이 화면에 시나리오 플래닝 4단계·액션 수단이 있는가 — 업무 영역(라우트)과 별개 축이다.
        //   시나리오 화면만 화면 컨텍스트로 자기 모듈 코드를 발행하므로 그 유무가 곧 판정이다.
        //   위 chatModule 과 같은 값을 본다 — 둘이 어긋나면 없는 액션을 제안하게 된다.
        //   백엔드는 이 값으로 단계·Action Catalog·시나리오 전용 도구를 붙일지 정한다.
        scenario_planning: !!screen?.modCode,
        // 이동 가능한 화면 목록 — 화면 종류를 가리지 않고 항상 싣는다.
        //   권한 필터를 통과한 화면만 들어가므로 LLM 이 접근 불가 화면을 제안할 수 없다.
        //   시나리오 화면에서 이걸 빼면 화면 검색 도구가 빈 카탈로그를 받고, 그러면 레지스트리
        //   전체(테넌트가 라이선스하지 않은 모듈 포함)의 화면 이름이 답변 본문에 노출된다.
        //   카탈로그는 프롬프트에 들어가지 않고 ContextVar 로 도구만 읽으므로 토큰 비용이 없다.
        menu_catalog: requestScreenNav({ catalog: true })?.items ?? [],
        // 시나리오 구성 상한 — 백엔드가 "추가 선택지를 만들지 말지" 판단하는 유일한 근거.
        //   ⚠️ maxScenarios 기본값은 Infinity(무정책 화면) — 유한할 때만 싣는다.
        ...(Number.isFinite(maxScenarios) ? {
          scenario_count:         composeScenarioCount,
          max_scenarios:          maxScenarios,
          scenario_limit_reached: composeScenarioCount >= maxScenarios,
        } : {}),
        // ── 화면 고유 필드 — plan_ver_id·scenarios·current_params 등은 전부 화면이 만든다 ──
        ...adapterCtx,
        // 화면 버전 채널 — 어댑터가 plan_ver_id 를 채우지 않는 화면(리포트·검토)은 버전 드롭다운이
        //   발행한 값으로 메운다. 어댑터가 채운 값은 건드리지 않는다. 백엔드는 이 값으로 어느 데이터
        //   (릴리즈 팩트 / 엔진 결과 원본)를 볼지 정하고 프롬프트에 화면 버전을 밝힌다.
        ...(adapterCtx.plan_ver_id == null ? screenVersionFields(chatModule) : {}),
        ...screenCtx,
      }, txt, gen, _stopStatus);
      // /clear 로 대화가 초기화됐으면(세대 변경) 이 응답은 폐기.
      if (chatGenRef.current !== gen) return { accepted: true };
      // 서버가 같은 세션의 이전 턴을 아직 처리 중이라고 알려온 경우 — 오류가 아니라 안내다.
      //   보낸 문장은 입력창에 돌려줘 그대로 재시도할 수 있게 한다.
      if (res?.busy) {
        _stopStatus();
        setTyping(false);
        dropSseBubble();
        emitDraft({ text: txt });
        typeMessage(t("MSG_ChatBusy"), []);
        return { accepted: true };
      }
      // 사용자 중지로 서버가 턴을 버렸다 — 빈 말풍선을 만들지 않는다.
      if (res?.cancelled) {
        _stopStatus();
        setTyping(false);
        dropSseBubble();
        return { accepted: true };
      }
      // 시나리오 구성 상한 도달 — 서버가 추가 계열 액션·선택지를 걷어내고 안내만 남긴 턴.
      if (res?.scenario_limit) {
        _stopStatus();
        setTyping(false);
        typeMessage(t("MSG_ScenarioLimitReached",
                      { max: res.scenario_limit.max ?? maxScenarios }), []);
        return { accepted: true };
      }
      const rawReply = res.reply ?? res.message;
      let fullText = "";
      if (typeof rawReply === "string") {
        fullText = rawReply;
      } else if (Array.isArray(rawReply)) {
        fullText = rawReply.map(i => (i && typeof i === "object" ? (i.text ?? "") : String(i ?? ""))).join("\n");
      } else if (rawReply != null) {
        try { fullText = JSON.stringify(rawReply); } catch { fullText = ""; }
      }
      const PLACEHOLDER = t("MSG_RequestFailedRetry");
      if (!fullText) fullText = PLACEHOLDER;
      // 드롭된 set_param 정정 문구 — 서버가 본문 말미에 ko 문구를 붙이고, 같은 순서로
      //   param_drop_notices([{key,args}])를 함께 준다. 키가 다 번역되면 그 말미를 갈아끼운다.
      //   (키가 없는 구버전 응답은 종전대로 ko 문구가 그대로 남는다.)
      let replyParts = res.reply_parts;
      const dropNotices = Array.isArray(res.param_drop_notices) ? res.param_drop_notices : [];
      if (dropNotices.length && fullText !== PLACEHOLDER) {
        const notes = dropNotices.map(n => keyedText(t, n?.key, "", n?.args)).filter(Boolean);
        if (notes.length === dropNotices.length) {
          const noteText = notes.join("\n\n");
          const blocks = fullText.split(/\n{2,}/);
          const body = blocks.slice(0, Math.max(0, blocks.length - dropNotices.length)).join("\n\n").trim();
          fullText = body ? `${body}\n\n${noteText}` : noteText;
          // 조각으로 끊어 그리는 경로 — 정정 문구는 서버가 마지막 조각으로 붙인다.
          if (Array.isArray(replyParts) && replyParts.length > 1) {
            replyParts = [...replyParts.slice(0, -1), noteText];
          }
        }
      }
      let actions = Array.isArray(res.actions) ? res.actions : [];
      // 되묻기 선택지(Human-in-the-loop) — 백엔드 parse_choices 가 검증한 클릭 리스트.
      const _rc = res.choices;
      let choices = (_rc && (_rc.input || (Array.isArray(_rc.items) && _rc.items.length >= 2)))
        ? _rc : null;

      // ── 관련 화면 링크 — 말풍선 하단 칩. **선택지 패널로 보내지 않는다.**
      //   선택지는 "골라서 답하라"는 요구로 읽혀, 조회만 원한 사용자를 붙잡는다. 화면 이동은
      //   눌러도 되고 안 눌러도 되는 링크여야 한다(백엔드도 promote_nav_screen 으로 같은 계약).
      //   여기서 미리 계산해 두는 이유: 아래 "본문 없으면 말풍선을 만들지 않는다" 조기 반환에
      //   걸리면 링크가 통째로 사라진다 — 그 조기 반환이 정확히 '조회 불가 + 링크만' 케이스다.
      //   구버전 백엔드·복원된 옛 턴은 승격 전 nav_screen 액션으로 오므로 함께 걷는다.
      //   두 소스를 미리 합쳐 한 번에 넘긴다 — 나눠 부르면 중복 제거와 id 부여가 갈라진다.
      const screenLinks = buildScreenLinks([
        ...(Array.isArray(res.screen_links) ? res.screen_links : []),
        ...(Array.isArray(res.actions) ? res.actions : [])
          .filter(a => a?.action === ACTION.NAV_SCREEN)
          .map(a => ({ key: a.payload?.key, screen: null, params: null })),
      ]);

      // 실행 가능한 액션은 버튼 없이 즉시 실행 (챗 명령 = 클릭 대체).
      let handledAny = false;   // 하나라도 성공 실행됐는지 — 거짓 실패 메시지 방지용
      const targetButtons = [];  // role 미지정 set_param → 메시지에 첨부할 시나리오 선택 칩
      const staleActions = [];   // 레지스트리에 없는 dispatch id
      const buildTargetButtons = (payload) => {
        const cands = editableScenarios();
        const mk = (role, label) => ({
          label, action: "set_param", style: "primary",
          payload: { field: payload.field, value: payload.value, role, displayValue: payload.displayValue, op: payload.op },
        });
        const btns = cands.map(c => mk(c.role, scnDisplay(c)));
        if (cands.length >= 2) btns.push(mk("all", t("agentApplyAll", { defaultValue: "전체 적용 (둘 다)" })));
        return btns;
      };
      const hasRealRole = (r) => !!r && r !== "auto";
      // 되돌리기 어려운 동작 — 확정(릴리즈)과 시나리오 제거는 **항상 클릭을 받고** 실행한다.
      const irreversibleAction = (action, dispatchId) =>
        IRREVERSIBLE_VERBS.has(action)
        || (dispatchId != null && adapter.isIrreversibleId(dispatchId));
      // 모델이 **곁들인** 이동 제안인가 — 답변 끝에 흐름 안내로 붙는 이동은 사용자가 시킨 것이 아니다.
      const suggestedNavigation = (a, dispatchId) =>
        a?.origin === ACTION_ORIGIN.LLM
        && (a.action === "navigate"
            || (dispatchId != null && adapter.isNavigationId(dispatchId)));
      // 본문이 승인을 되묻는 제안인가 — 그렇다면 같은 턴에 값을 자동 적용하지 않는다.
      const askingConfirm = !!choices
        || /(할까요|하시겠어요|하시겠습니까|드릴까요|적용하시겠|진행하시겠|적용할까|진행할까|변경할까)/.test(fullText || "");
      if (onExecuteAction && actions.length > 0) {
        const confirms = [];
        actions = actions.filter(a => {
          // 레지스트리 dispatch — 백엔드가 고른 id 를 화면이 선언한 run 으로 실행
          if (a.action === "dispatch") {
            const _id = a.payload?.id ?? a.id;
            const act = liveActions.find(x => x.id === _id);
            if (act) {
              if (irreversibleAction(null, _id) || suggestedNavigation(a, _id)) {
                if (!a.label) a.label = act.ko || act.label || "";
                return true;
              }
              handledAny = true;
              Promise.resolve().then(() => act.run?.(a.payload?.args));
              if (act.confirmKo) confirms.push(act.confirmKo);
              return false;
            }
            staleActions.push(_id);
            console.warn("[AgentChat] dispatch id not in registry — dropped:", _id);
            return false;
          }
          if (irreversibleAction(a.action)) return true;
          if (suggestedNavigation(a)) return true;
          // 메뉴 화면 이동 — 출처(origin)와 무관하게 **항상 클릭을 받는다**. 작업 중인 화면이
          //   예고 없이 바뀌면 안 되므로 fastpath 로 와도 자동 실행하지 않는다.
          if (a.action === ACTION.NAV_SCREEN) return true;
          // 커스텀 시나리오 등록 — 되묻는 턴 여부와 무관하게 **항상 클릭 버튼**으로만 노출한다.
          if (a.action === "save_custom_scenario") return true;
          if (askingConfirm && (a.action === "set_param" || a.action === "run_engine" || a.action === "apply_expert_case")) {
            if (a.action === "set_param") {
              if (!hasRealRole(a.payload?.role)) {
                targetButtons.push(...buildTargetButtons(a.payload));
                return false;
              }
              const sc = (scenarios || []).find(s => s.role === a.payload.role);
              if (sc?.role === "base") return false;
              const disp = sc ? scnDisplay(sc) : a.payload.role;
              targetButtons.push({
                label: `${disp}: ${a.payload.displayValue ?? t("changeDefaultLabel")}`,
                action: "set_param", style: "primary", payload: a.payload,
              });
              return false;
            }
            return true;   // run_engine → 라벨 있는 버튼으로 유지(클릭 시 실행)
          }
          if (askingConfirm && (a.action === "deselect_scenario" || a.action === "select_scenario")) {
            return true;
          }
          // 값은 명확하나 대상 시나리오(role)가 미지정인 set_param → 후보 수에 따라 처리.
          if (a.action === "set_param" && !hasRealRole(a.payload?.role)) {
            const cands = editableScenarios();
            if (cands.length === 0) {
              setPendingParamApply({ field: a.payload.field, value: a.payload.value, displayValue: a.payload.displayValue, label: a.payload.displayValue, op: a.payload.op, owner: ownerKey });
              if (onExecuteAction) { try { onExecuteAction({ action: "navigate", payload: { step: 1 } }); } catch {} }
              if (!fullText || fullText === PLACEHOLDER) fullText = t("MSG_NoScenarioAutoApplyShort");
              handledAny = true;
              return false;
            }
            if (cands.length === 1) {
              const c = cands[0];
              let ok = false;
              try { ok = !!onExecuteAction({ action: "set_param", payload: { field: a.payload.field, value: a.payload.value, role: c.role, displayValue: a.payload.displayValue, op: a.payload.op } }); } catch { ok = false; }
              if (ok) confirms.push(t("MSG_AppliedToTarget", { target: scnDisplay(c), value: a.payload.displayValue ?? t("changeDefaultLabel") }));
              handledAny = true;
              return false;
            }
            targetButtons.push(...buildTargetButtons(a.payload));
            handledAny = true;
            return false;
          }
          if (!onExecuteAction(a)) return true;  // 미처리 → 버튼 유지
          handledAny = true;
          if (a.action === "set_param" && a.payload?.displayValue)
            confirms.push(t("MSG_ChangedToValue", { value: a.payload.displayValue }));
          else if (a.action === "run_engine")
            confirms.push(t("MSG_RunningEngine"));
          else if (a.action === "apply_expert_case")
            confirms.push(t("MSG_ComposedFromNamedCase", { name: a.payload?.scenario_name || t("caseDefaultLabel") }));
          else if (a.action === "select_mode")
            confirms.push(t("MSG_ComposedByMethod", { method: a.payload?.displayValue || t("selectedDefaultLabel") }));
          else if (a.action === "navigate") {
            const st = a.payload?.step;
            confirms.push(typeof st === "number" && stepNameKeys[st]
              ? t("agentNavMovedToStep", { step: t(stepNameKeys[st]) }) : t("agentNavMovedGeneric"));
          }
          return false;  // 실행됨 → 버튼 제거
        });
        // LLM 본문이 비어있으면 실행 확인 메시지로 대체 (본문이 있으면 그대로 유지)
        if (confirms.length && (!fullText || fullText === PLACEHOLDER)) {
          fullText = confirms.join("\n");
        }
        // 실행할 수 있는 게 하나도 없었고 낡은 id 만 왔다 — 무반응 대신 이유를 알린다.
        if (staleActions.length && !handledAny && (!fullText || fullText === PLACEHOLDER)) {
          fullText = t("MSG_ActionUnavailableHere");
        }
      }
      // role 미지정 set_param → 시나리오 선택지를 함께 제시. 본문이 비면 기본 질문으로 대체.
      const msgActions = targetButtons.length ? [...actions, ...targetButtons] : actions;
      if (targetButtons.length && (!fullText || fullText === PLACEHOLDER)) {
        fullText = t("MSG_WhichScenarioToApplyShort");
      }
      // 화면 이동은 선택지가 아니라 링크 칩이다 — 여기서 한 번 더 갈라낸다.
      //   걷어내지 않으면 아래 변환이 "이동하시겠어요?" 선택지를 다시 만든다.
      //   칩으로는 위에서 이미 걷었다(screenLinks).
      const panelActions = msgActions.filter(b => b.action !== ACTION.NAV_SCREEN);
      // 실행 가능한 액션은 말풍선 버튼이 아니라 **선택지 패널**로 낸다.
      if (!choices && panelActions.length) {
        // 표시 라벨 — 백엔드는 라벨을 비워 보낸다(다국어 문구는 프론트 소유).
        //   ⚠️ 전역 챗은 위 실행 필터 블록(onExecuteAction 이 있어야 돈다)을 타지 않아 dispatch
        //      라벨이 채워지지 않는다. 폴백을 두지 않으면 "이전 화면으로" 칩이 **"적용했습니다"**
        //      라는 이름으로 뜬다(실사고).
        const btnLabel = (b) => {
          const lbl = choiceLabel(b);
          if (lbl) return lbl;
          if (b.action === ACTION.NAV_SCREEN && b.payload?.name)
            return t("agentNavMoveToScreenLabel", { screen: b.payload.name });
          if (b.action === "dispatch") {
            const act = liveActions.find(x => x.id === (b.payload?.id ?? b.id));
            if (act) return actLabel(act);
          }
          return t("MSG_Applied");
        };
        choices = {
          prompt: (fullText && fullText !== PLACEHOLDER) ? "" : t("choicesDefaultPrompt"),
          items: panelActions.map((b, i) => ({
            id: `a${i + 1}`, label: btnLabel(b),
            kind: "action", action: { action: b.action, payload: b.payload, origin: b.origin },
          })),
        };
      }
      if (choices) {
        // 시나리오 구성 선택지는 구성 단계에서 고르게 한다 — 고르기 전에 화면을 먼저 맞춰 둔다.
        if (composeStep != null && choicesCompose(choices) && onExecuteAction && currentStep !== composeStep) {
          try { onExecuteAction({ action: "navigate", payload: { step: composeStep } }); } catch {}
        }
        // 보낸 화면을 찍어 둔다 — 응답이 탭을 옮긴 뒤 도착하면 이 선택지는 **다른 화면**에 뜬다.
        //   ownerKey 는 이 send 를 부른 렌더의 값(보낸 화면)이다. handleChoiceClick 이 대조한다. [Q-131]
        setPendingChoices({ ...choices, originOwner: ownerKey });
        setChoiceTrail(preTrail(choices));   // 발화가 이미 정한 앞 단계가 있으면 경로에 표시
        choiceBusyRef.current = false;
        const norm = (s) => String(s || "").replace(/\s+/g, "").trim();
        if (!fullText || fullText === PLACEHOLDER || norm(fullText) === norm(choices.prompt)) {
          fullText = "";
        }
      }
      // 전문가 검증 사례 카드 — 백엔드 get_expert_cases 도구가 검색했을 때만 동봉.
      const expertCases = Array.isArray(res.expert_cases) ? res.expert_cases : [];
      // 커스텀 시나리오 초안 카드 — draft_custom_scenario 도구가 변환했을 때만 동봉.
      const customDraft = res.custom_scenario_draft ?? null;
      if (customDraft) {
        customDraft._eid = `cd${Date.now()}`;
        const cat = Array.isArray(customDraft.param_catalog) ? customDraft.param_catalog : [];
        const meta = {};
        Object.entries(customDraft.params || {}).forEach(([k, node]) => {
          const c = cat.find(x => x?.key === k) || {};
          meta[k] = {
            label: node?.label ?? c.label, label_key: node?.label_key ?? c.label_key,
            unit: node?.unit ?? c.unit, unit_key: node?.unit_key ?? c.unit_key,
            type: node?.type ?? c.type, options: node?.options ?? c.options,
            min: c.min ?? null, max: c.max ?? null,
          };
        });
        const regPayload =
          (res.choices?.items || []).find(i => i?.action?.action === "save_custom_scenario")?.action?.payload
          ?? (Array.isArray(res.actions) ? res.actions : [])
               .find(a => a?.action === "save_custom_scenario")?.payload
          ?? null;
        draftEditRef.current = regPayload ? { payload: regPayload, meta, eid: customDraft._eid } : null;
      }
      _stopStatus();
      setTyping(false);
      // 본문이 없어도 링크가 있으면 말풍선을 만든다 — 칩을 얹을 자리가 사라지기 때문이다.
      //   짧은 안내 한 줄로 대체한다(빈 말풍선에 칩만 뜨면 무슨 답인지 알 수 없다).
      if ((!fullText || fullText === PLACEHOLDER) && screenLinks.length
          && expertCases.length === 0 && !customDraft) {
        fullText = t("agentLinkOnlyBody");
      }
      // 액션은 성공 처리됐는데 표시할 본문/confirm·사례가 없으면 거짓 실패 메시지를 띄우지 않는다.
      if ((!fullText || fullText === PLACEHOLDER) && expertCases.length === 0
          && !customDraft && (handledAny || choices)) {
        dropSseBubble();   // 델타로 그리던 말풍선이 있으면 지운다(빈 말풍선이 남지 않게)
        return { accepted: true };
      }
      // extra: 참고 데이터 칩(consulted) + 관련 화면 링크 + 피드백 좌표(seq/sessionId).
      const _extra = {};
      if (screenLinks.length) _extra.screenLinks = screenLinks;
      if (Array.isArray(res.data_sources) && res.data_sources.length) {
        _extra.consulted = res.data_sources.map(k => ({ label: k }));
      } else if (!res.refused && consultedRef.current.length) {
        // 거절된 턴은 서버가 참고 데이터를 빼고 보낸다 — 스트리밍 중 모은 라벨로 되살리지 않는다.
        _extra.consulted = [...consultedRef.current];
      }
      // 도구 이력 그룹을 닫는 표식 + 접힌 줄 라벨. 라벨은 **i18n 키를 담은 인자**로 온다
      //   (문자열이 아니다) — 언어를 바꾸면 지난 말풍선도 다시 번역된다.
      _extra.turnId = turnIdRef.current;
      if (res.tool_summary_args) _extra.toolSummaryArgs = res.tool_summary_args;
      if (res.seq != null) {
        _extra.seq = res.seq;
        _extra.sessionId = res.session_id || chatSessionIdRef.current;
      }
      // 도구 때문에 끊어 둔 말풍선이 있으면 라운드 조각을 배분하고, 마지막 조각만 확정한다
      //   (통짜 fullText 를 그대로 넣으면 이미 보여준 서두가 한 번 더 찍힌다).
      const headText = applyReplyParts(replyParts, res.reply_part_kinds,
                                     fullText, turnIdRef.current);
      // 마지막 라운드가 ```choices 펜스뿐이면 조각이 비어 온다 — 빈 말풍선을 남기지 않는다.
      //   앞서 끊어 둔 본문·도구 말풍선은 그대로 두므로 대화 기록은 사라지지 않는다.
      if (!headText && expertCases.length === 0 && !customDraft && !screenLinks.length) {
        dropSseBubble();
        invalidateSessions();
        return { accepted: true };
      }
      typeMessage(headText || (screenLinks.length ? t("agentLinkOnlyBody") : headText),
                  [], expertCases, customDraft,
                  Object.keys(_extra).length ? _extra : null);
      // 첫 턴이 저장되면 세션 목록의 제목·시각이 바뀐다 — 다음 조회 때 새로 받게 한다.
      invalidateSessions();
      return { accepted: true };
    } catch (e) {
      if (chatGenRef.current !== gen) return { accepted: true };
      // 스트림 중단(언마운트·/clear·모듈 전환)은 실패가 아니다.
      if (e?.name === "AbortError") { _stopStatus(); setTyping(false); return { accepted: true }; }
      _stopStatus();
      setTyping(false);
      // 스트림이 중간에 끊긴 경우 — 받은 부분은 남기고 중단 사실을 덧붙인다(서버 오류와 구분).
      if (e?.code === "STREAM_TRUNCATED") {
        typeMessage((e.partial ? e.partial + "\n\n" : "") + t("MSG_ResponseInterrupted"));
        return { accepted: true };
      }
      console.warn("[AgentChat] 턴 실패:", e);
      typeMessage(chatErrorText(t, e) || t("MSG_ServerResponseFailed"));
      return { accepted: true };
    } finally {
      // 성공·실패·중간 return 모두 여기를 지난다 — 하나라도 새면 입력이 영구히 잠긴다.
      if (chatGenRef.current === gen) markChatBusy(false);
    }
  };

  // ── 화면 전환 대응 ─────────────────────────────────────────────────────────
  // 화면이 바뀌면 휘발성 UI 는 버린다 — 되묻기 선택지·대기 액션은 그 화면의 액션 레지스트리를
  //   전제로 만들어진 것이라, 다른 화면에서 그대로 실행하면 엉뚱한 대상에 적용된다.
  //   (대화 내용 자체는 사용자 귀속이므로 유지한다)
  const didMountScreenRef = useRef(false);
  useEffect(() => {
    if (!didMountScreenRef.current) { didMountScreenRef.current = true; return; }
    setPendingChoices(null);
    setChoiceTrail([]);
    setChoiceInput("");
    choiceBusyRef.current = false;
    bumpUi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenKey]);

  /*
    대화 도중 모듈이 바뀌면(홈 GENERAL → DP 시나리오 화면 등) 백엔드 페르소나·도구도 바뀐다.
    ⚠️ 그렇다고 대화에 "이제 OO 기준으로 답합니다" 같은 줄을 남기지 않는다 — 그건 라우팅 구현
      사실이지 사용자의 관심사가 아니다. 사용자가 직접 옮긴 화면이라 이미 알고 있고, 아무 행동도
      제안하지 않으며, 여러 모듈을 거치면 대화가 그 줄로 도배된다. 기억은 세션 단위로 이어지므로
      사용자 관점에서는 실제로 달라진 것도 없다.
      지금 어느 모듈 기준인지는 **패널 헤더의 컨텍스트 칩**이 상시 보여준다(대화 오염 없이).
    취소를 올바른 모듈로 보내는 것은 별개 문제이고 turnModRef 가 담당한다.
  */

  // ── 화면 → 챗 명령형 API (notify / promptChoices) ──────────────────────────
  //   예전엔 패널의 useImperativeHandle 이었다. 엔진으로 올라오면서 **챗을 한 번도 열지 않아도**
  //   화면의 notify() 가 유실되지 않는다(전엔 패널 미마운트 시 조용히 증발했다).
  const apiRef = useAgentChatApiRef();
  useLayoutEffect(() => {
    if (!apiRef) return undefined;
    apiRef.current = {
      notify: (text) => typeMessage(text, []),
      promptChoices: ({ message, prompt, items } = {}) => {
        const list = (items || []).filter(Boolean);
        if (!list.length) return;
        if (message) typeMessage(message, []);
        setPendingChoices({
          prompt: prompt || t("choicesDefaultPrompt"),
          items: list.map((it, i) => ({
            id: it.id || `api${i + 1}`,
            label: it.label,
            onClick: () => { try { it.run?.(); } catch {} },
          })),
        });
        setChoiceTrail([]);
        choiceBusyRef.current = false;
      },
    };
    return () => { apiRef.current = null; };
  }, [apiRef, typeMessage, t]);

  // ── 명령 구현 등록 + 뷰별 API 래퍼 ─────────────────────────────────────────
  implRef.current = {
    send, abortChat, resetChat, clearConversation,
    loadChatSession, deleteChatSession, refreshSessions,
    sendFeedback, handleChoiceClick, goBackChoice, dismissChoices,
    submitChoiceInput, setChoiceInput, runLiveAction, runPaletteAction,
    requestParamTarget, typeMessage, requestScreenNav, notifyBlocked,
    openScreenLink, navBack,
  };

  // 뷰별 래퍼 — 호출한 뷰를 기록해 두었다가 초안 되돌리기·포커스를 그 뷰로만 돌려준다.
  //   identity 는 viewId 당 영구 고정이라 소비처의 useCallback deps 를 오염시키지 않는다.
  const apiFactory = useMemo(() => {
    const cache = new Map();
    return (viewId) => {
      if (cache.has(viewId)) return cache.get(viewId);
      const o = {};
      API_NAMES.forEach(n => {
        o[n] = (...args) => {
          activeViewRef.current = viewId;
          return implRef.current?.[n]?.(...args);
        };
      });
      cache.set(viewId, o);
      return o;
    };
  }, []);

  // ── 컨텍스트 값 4분할 ──────────────────────────────────────────────────────
  //   ⚠️ 아래 useMemo 의 deps 정확성이 이 설계의 유일한 성능 계약이다. 하나라도 빠뜨려
  //     매 렌더 새 객체가 되면 SSE 델타마다 홈 화면 전체가 재렌더된다.
  const hasConversation = messages.length > 1;
  /*
    현재 대화를 **한 줄로 가리키는 이름** — 첫 사용자 발화. 서버가 세션 title 을 정하는 규칙과
    같다(chat_store.save_turn 이 첫 턴의 user_message 를 title 로 굳힌다).
    ⚠️ 여기서 파생해 두는 이유: 홈 카드는 성능 때문에 messages 를 구독하지 않는데,
      "지금 어떤 대화가 진행 중인가"를 보여주려면 그 이름이 필요하다. 값은 첫 발화 이후
      바뀌지 않는 문자열이라, 델타마다 재계산되어도 ui 컨텍스트의 identity 는 흔들리지 않는다.
  */
  const conversationTitle = useMemo(
    () => messages.find(m => m.role === "u")?.text ?? "", [messages]);
  const status = useMemo(() => ({
    typing, statusText, chatBusy, chatBusyBlocked, chatAborting, loadingSession,
    hasConversation, draftSignal, uiEpoch, historyEpoch, liveTurnId,
  }), [typing, statusText, chatBusy, chatBusyBlocked, chatAborting, loadingSession,
       hasConversation, draftSignal, uiEpoch, historyEpoch, liveTurnId]);

  const ui = useMemo(() => ({
    pendingChoices, choiceTrail, choiceInput, canGoBackChoice,
    feedbackGiven, sessionId, sessions, conversationTitle, liveActions, actLabel,
    modCode, standalone: !screen,
  }), [pendingChoices, choiceTrail, choiceInput, canGoBackChoice,
       feedbackGiven, sessionId, sessions, conversationTitle, liveActions, actLabel, modCode, screen]);

  return { messages, status, ui, apiFactory };
}
